import { supabase } from '../supabase/client';
import { Craftsman, ServiceRequest } from '../../types';
import {
  EmployeeJobService,
  companyFinancePaymentReceivedAmount,
  companyFinancePaymentRecognizedAt,
  companyFinancePaymentNetAmount
} from './employeeJobService';
import { resolveIsCompanyFixedForRequest } from '../payment/paymentService';
import { resolveCraftsmanAvailability, availabilitySortRank } from '../../utils/craftsmanAvailability';
import { resolveCraftsmanBadgeLevel, resolveCraftsmanBadgeLevelForRow } from '../../utils/craftsmanBadgeLevel';
import { CommissionService } from '../commission/commissionService';
import { PAYMENT_CONFIG } from '../../config/payment';
import { resolveProfileImageUrl } from '../../utils/profileImageUrl';
import { isUsableProviderLocation, isUsableUserGeoLocation } from '../../utils/geoDefaults';
import { matchesServiceAreaList } from '../../constants/taiwanServiceAreas';
import { ExposureService } from './exposureService';

// 檢查是否為有效的 UUID
const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

export class CraftsmanService {
  /** 與付款流程一致：統包模式或「有公司、無有效工匠」視為 company_fixed 計佣 */
  private static rowIsCompanyFixed(row: {
    pricing_mode?: string | null;
    company_id?: string | null;
    craftsman_id?: string | null;
  }): boolean {
    return resolveIsCompanyFixedForRequest(
      {
        pricing_mode: row.pricing_mode ?? undefined,
        companyId: row.company_id ?? undefined,
        company_id: row.company_id,
        craftsmanId: row.craftsman_id ?? undefined,
        craftsman_id: row.craftsman_id,
      } as ServiceRequest,
      { companyIdOverride: row.company_id ?? null }
    );
  }

  private static computeAvailability(
    craftsman: any,
    at?: Date
  ): {
    availability: 'available' | 'busy' | 'resting' | 'off_hours';
    isOnBreak: boolean;
  } {
    return resolveCraftsmanAvailability(craftsman, at ?? new Date());
  }

  /** 從 service_requests 統計已完成案件（與儀表板 getDashboardStats 一致，不用 craftsmen.completed_jobs 快取） */
  static async countCompletedJobs(craftsmanId: string): Promise<number> {
    const { count, error } = await supabase
      .from('service_requests')
      .select('id', { count: 'exact', head: true })
      .eq('craftsman_id', craftsmanId)
      .eq('status', 'completed');

    if (error) {
      console.error('統計已完成案件失敗:', error);
    } else if ((count ?? 0) > 0) {
      return count ?? 0;
    }

    // 備援：公司統包等工單可能未寫入 craftsman_id，但評價已關聯工匠
    const { data: reviewRows, error: reviewError } = await supabase
      .from('reviews')
      .select('service_request_id')
      .eq('craftsman_id', craftsmanId)
      .not('service_request_id', 'is', null);

    if (reviewError || !reviewRows?.length) {
      return count ?? 0;
    }

    const requestIds = [
      ...new Set(
        reviewRows
          .map((r) => r.service_request_id)
          .filter((id): id is string => Boolean(id))
      ),
    ];

    const { count: viaReviews, error: viaReviewsError } = await supabase
      .from('service_requests')
      .select('id', { count: 'exact', head: true })
      .in('id', requestIds)
      .eq('status', 'completed');

    if (viaReviewsError) {
      console.error('透過評價統計已完成案件失敗:', viaReviewsError);
      return count ?? 0;
    }

    return viaReviews ?? count ?? 0;
  }

  /** 與 commission-stats / calculate_commission_accurate 對齊：優先 payments.craftsman_earnings，無則依 actual_price 推算 */
  private static sumPaymentsCraftsmanEarnings(
    paymentRows: Array<{ craftsman_earnings?: number | null }>
  ): number {
    return paymentRows.reduce((sum, r) => sum + (Number(r.craftsman_earnings) || 0), 0);
  }

  private static resolveRequestCraftsmanEarnings(
    paymentRows: Array<{ craftsman_earnings?: number | null }>,
    serviceRequest:
      | {
          actual_price?: number | string | null;
          estimated_price?: number | string | null;
          urgency?: string | null;
          category_id?: number | string | null;
          pricing_mode?: string | null;
          company_id?: string | null;
          craftsman_id?: string | null;
        }
      | null
      | undefined,
    isPremium: boolean
  ): number {
    const fromDb = this.sumPaymentsCraftsmanEarnings(paymentRows);
    if (fromDb > 0) {
      return Math.round(fromDb * 100) / 100;
    }

    const actualPrice =
      serviceRequest?.actual_price != null ? Number(serviceRequest.actual_price) : 0;
    if (actualPrice > 0) {
      try {
        const commissionDetails = CommissionService.calculate({
          serviceFee: actualPrice,
          isPremium,
          isEmergency: serviceRequest?.urgency === 'urgent',
          categoryId:
            serviceRequest?.category_id != null
              ? Number(serviceRequest.category_id)
              : undefined,
          isCompanyFixed: serviceRequest
            ? CraftsmanService.rowIsCompanyFixed(serviceRequest)
            : false,
        });
        return commissionDetails.craftsmanEarnings;
      } catch (error) {
        console.error('resolveRequestCraftsmanEarnings 計算失敗:', error);
      }
    }

    const estimated =
      serviceRequest?.estimated_price != null ? Number(serviceRequest.estimated_price) : 0;
    if (estimated > 0) {
      try {
        const commissionDetails = CommissionService.calculate({
          serviceFee: estimated,
          isPremium,
          isEmergency: serviceRequest?.urgency === 'urgent',
          categoryId:
            serviceRequest?.category_id != null
              ? Number(serviceRequest.category_id)
              : undefined,
          isCompanyFixed: serviceRequest
            ? CraftsmanService.rowIsCompanyFixed(serviceRequest)
            : false,
        });
        return commissionDetails.craftsmanEarnings;
      } catch (error) {
        console.error('resolveRequestCraftsmanEarnings 預估價計算失敗:', error);
      }
    }

    return 0;
  }

  /** 搜尋篩選「工匠狀態」：與 computeAvailability 一致（不依賴僅 DB status 字串） */
  private static matchesSearchAvailabilityFilter(
    craftsman: any,
    filter?: string,
    at?: Date
  ): boolean {
    if (!filter || filter === 'all') return true;
    const { availability } = this.computeAvailability(craftsman, at);
    if (filter === 'available') return availability === 'available';
    if (filter === 'busy') return availability === 'busy';
    return true;
  }

  private static convertProfileImageUrl(profileImage: string | null | undefined): string {
    return resolveProfileImageUrl(profileImage);
  }

  private static parseWorkingHours(raw: any): Record<string, { enabled: boolean; startTime: string; endTime: string }> | undefined {
    if (!raw) return undefined;
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch (error) {
        console.warn('Unable to parse working_hours string:', raw, error);
        return undefined;
      }
    }
    if (typeof raw === 'object') {
      return raw as Record<string, { enabled: boolean; startTime: string; endTime: string }>;
    }
    return undefined;
  }

  /** 合併 working_hours 後依與儀表板相同規則重算 availability */
  private static reapplyAvailabilityAfterHours(
    craftsmen: Craftsman[],
    at?: Date
  ): Craftsman[] {
    const ref = at ?? new Date();
    return craftsmen.map((c) => {
      const ctx = c._availabilityContext;
      if (!ctx) return c;
      const { availability, isOnBreak } = resolveCraftsmanAvailability(
        {
          status: ctx.status,
          is_on_break: ctx.is_on_break,
          working_hours: c.workingHours,
        },
        ref
      );
      const { _availabilityContext: _drop, ...rest } = c;
      return { ...rest, availability, isOnBreak };
    });
  }

  private static async populateWorkingHours(
    craftsmen: Craftsman[],
    availabilityAt?: Date
  ): Promise<Craftsman[]> {
    const missingIds = craftsmen
      .filter(craftsman => !craftsman.workingHours || Object.keys(craftsman.workingHours).length === 0)
      .map(craftsman => craftsman.id);

    if (missingIds.length === 0) {
      return this.reapplyAvailabilityAfterHours(craftsmen, availabilityAt);
    }

    try {
      const { data, error } = await supabase
        .from('craftsmen')
        .select('id, working_hours')
        .in('id', missingIds);

      if (error || !data) {
        if (error) {
          console.warn('Unable to fetch working hours for craftsmen:', error);
        }
        return this.reapplyAvailabilityAfterHours(craftsmen, availabilityAt);
      }

      const hoursMap = data.reduce<Record<string, Record<string, { enabled: boolean; startTime: string; endTime: string }> | undefined>>(
        (acc, item: any) => {
          acc[item.id] = this.parseWorkingHours(item.working_hours);
          return acc;
        },
        {}
      );

      const merged = craftsmen.map(craftsman => {
        if (craftsman.workingHours && Object.keys(craftsman.workingHours).length > 0) {
          return craftsman;
        }
        return {
          ...craftsman,
          workingHours: hoursMap[craftsman.id] || craftsman.workingHours,
        };
      });
      return this.reapplyAvailabilityAfterHours(merged, availabilityAt);
    } catch (error) {
      console.warn('Exception while populating working hours:', error);
      return this.reapplyAvailabilityAfterHours(craftsmen, availabilityAt);
    }
  }

  /** get_nearby_craftsmen RPC 不傳回 service_areas；批次補齊供前端依服務區域篩選。 */
  private static async mergeServiceAreasFromDb(rows: any[]): Promise<any[]> {
    if (!rows?.length) return rows;
    const ids = [...new Set(rows.map((c: any) => c?.id).filter(Boolean))] as string[];
    if (ids.length === 0) return rows;

    const { data, error } = await supabase
      .from('craftsmen')
      .select('id, service_areas')
      .in('id', ids);

    if (error) {
      console.warn('mergeServiceAreasFromDb:', error.message);
      return rows;
    }
    if (!data?.length) return rows;

    const map: Record<string, unknown> = {};
    data.forEach((r: any) => {
      map[r.id] = r.service_areas;
    });

    return rows.map((c: any) => ({
      ...c,
      service_areas: map[c.id] ?? c.service_areas ?? c.serviceAreas,
    }));
  }

  // 獲取附近的工匠
  static async getNearbyCraftsmen(
    location: { lat: number; lng: number },
    radius: number = 10
  ): Promise<Craftsman[]> {
    try {
      console.log('�� getNearbyCraftsmen 被調用');
      console.log('📍 位置:', location);
      console.log('📏 搜索半徑:', radius, 'km');

      if (!isUsableUserGeoLocation(location)) {
        console.warn('⚠️ 略過 getNearbyCraftsmen：無有效使用者定位');
        return [];
      }
      
      // 使用 PostGIS 查詢附近的工匠
      const { data, error } = await supabase
        .rpc('get_nearby_craftsmen', {
          user_lat: location.lat,
          user_lng: location.lng,
          search_radius: radius
        });

      if (error) {
        console.error('❌ Error fetching nearby craftsmen:', error);
        return [];
      }

      console.log('✅ RPC 返回數據:', data);
      console.log('📊 找到工匠數量:', data?.length || 0);
      
      // 詳細日誌：檢查每個工匠的數據
      if (data && data.length > 0) {
        data.forEach((craftsman: any, index: number) => {
          console.log(`🔍 工匠 ${index + 1}:`, {
            id: craftsman.id,
            name: craftsman.name,
            status: craftsman.status,
            location: craftsman.location,
            locationType: typeof craftsman.location,
            hasLocation: !!craftsman.location
          });
        });
      } else {
        console.warn('⚠️ RPC 返回空數組，可能原因：');
        console.warn('  1. 沒有符合條件的工匠（status != pending）');
        console.warn('  2. 工匠距離超出搜索半徑');
        console.warn('  3. 工匠沒有位置信息');
      }

      const enrichedRows = await this.mergeServiceAreasFromDb(data || []);

      const result = enrichedRows.map((craftsman: any) => {
        const { availability, isOnBreak } = this.computeAvailability(craftsman);
        
        // 正確解析 location JSONB
        let location = { lat: 0, lng: 0 };
        if (craftsman.location) {
          try {
            // location 可能是 JSONB 對象或字符串
            let loc = craftsman.location;
            if (typeof loc === 'string') {
              loc = JSON.parse(loc);
            }
            
            if (loc && typeof loc === 'object') {
              const lat = loc.lat ?? loc.latitude;
              const lng = loc.lng ?? loc.longitude;
              
              if (lat != null && lng != null) {
                location = {
                  lat: typeof lat === 'number' ? lat : parseFloat(String(lat)),
                  lng: typeof lng === 'number' ? lng : parseFloat(String(lng)),
                };
                
                // 驗證位置數據有效性
                if (isNaN(location.lat) || isNaN(location.lng)) {
                  console.warn('⚠️ 工匠位置數據無效:', craftsman.id, { lat, lng });
                  location = { lat: 0, lng: 0 };
                } else {
                  console.log('✅ 成功解析工匠位置:', craftsman.id, location);
                }
              } else {
                console.warn('⚠️ 工匠位置缺少 lat/lng:', craftsman.id, loc);
              }
            } else {
              console.warn('⚠️ 工匠位置格式不正確:', craftsman.id, typeof loc, loc);
            }
          } catch (error) {
            console.error('❌ 解析工匠位置失敗:', craftsman.id, craftsman.location, error);
          }
        } else {
          console.warn('⚠️ 工匠沒有位置信息:', craftsman.id);
        }
        
        return {
          id: craftsman.id,
          name: craftsman.name,
          profileImage: this.convertProfileImageUrl(craftsman.profile_image),
          rating: craftsman.rating || 0,
          numReviews: craftsman.total_reviews || 0,
          specialties: craftsman.specialties || [],
          location,
          availability,
          responseTime: '5 min',
          hourlyRate: craftsman.hourly_rate || 0,
          isPremium: craftsman.is_premium || false,
          premiumUntil: craftsman.premium_until ? new Date(craftsman.premium_until) : undefined,
          totalEarnings: craftsman.total_earnings || 0,
          completedJobs: craftsman.completed_jobs || 0,
          accountBalance: craftsman.account_balance || 0,
          isOnBreak,
          workingHours: this.parseWorkingHours(craftsman.working_hours),
          serviceAreas: craftsman.service_areas || craftsman.serviceAreas || [],
          _availabilityContext: { status: craftsman.status, is_on_break: craftsman.is_on_break },
        };
      });
      
      const withHours = await this.populateWorkingHours(result as Craftsman[]);
      const withValidLocation = withHours.filter((c) => isUsableProviderLocation(c.location));
      console.log('�� 最終返回工匠:', withValidLocation);
      return withValidLocation;
    } catch (error) {
      console.error('❌ Exception in getNearbyCraftsmen:', error);
      return [];
    }
  }

  private static parseCraftsmanLocation(raw: unknown): { lat: number; lng: number } {
    if (!raw) return { lat: 0, lng: 0 };
    try {
      let loc = raw;
      if (typeof loc === 'string') loc = JSON.parse(loc);
      if (loc && typeof loc === 'object') {
        const o = loc as Record<string, unknown>;
        const lat = o.lat ?? o.latitude;
        const lng = o.lng ?? o.longitude;
        if (lat != null && lng != null) {
          const latN = typeof lat === 'number' ? lat : parseFloat(String(lat));
          const lngN = typeof lng === 'number' ? lng : parseFloat(String(lng));
          if (Number.isFinite(latN) && Number.isFinite(lngN)) {
            return { lat: latN, lng: lngN };
          }
        }
      }
    } catch {
      /* ignore */
    }
    return { lat: 0, lng: 0 };
  }

  private static async fetchCraftsmanRowByIdOrUserId(
    id: string
  ): Promise<{ row: Record<string, unknown> | null; error: string | null }> {
    const byCraftsmanId = await supabase.from('craftsmen').select('*').eq('id', id).maybeSingle();
    if (byCraftsmanId.data) {
      return { row: byCraftsmanId.data as Record<string, unknown>, error: null };
    }
    if (byCraftsmanId.error && byCraftsmanId.error.code !== 'PGRST116') {
      console.error('Error fetching craftsman by id:', byCraftsmanId.error);
    }

    const byUserId = await supabase.from('craftsmen').select('*').eq('user_id', id).maybeSingle();
    if (byUserId.data) {
      console.warn('[getCraftsmanById] 以 user_id 解析工匠列（URL 可能誤傳 user id）:', id);
      return { row: byUserId.data as Record<string, unknown>, error: null };
    }
    if (byUserId.error && byUserId.error.code !== 'PGRST116') {
      console.error('Error fetching craftsman by user_id:', byUserId.error);
      return { row: null, error: byUserId.error.message };
    }

    return { row: null, error: '找不到工匠資料' };
  }

  // 獲取工匠詳情
  static async getCraftsmanById(id: string): Promise<Craftsman | null> {
    try {
      console.log('获取工匠详情，ID:', id);

      const { row: craftsmanData, error: resolveError } = await this.fetchCraftsmanRowByIdOrUserId(id);
      if (!craftsmanData) {
        if (resolveError) console.warn('getCraftsmanById:', resolveError);
        return null;
      }

      const userId = craftsmanData.user_id as string | undefined;
      let userData: {
        name?: string;
        email?: string;
        phone?: string;
        profile_image?: string;
      } | null = null;

      if (userId) {
        const { data: user, error: userError } = await supabase
          .from('users')
          .select('name, email, phone, profile_image')
          .eq('id', userId)
          .maybeSingle();

        console.log('用户数据查询结果:', { user, userError });

        if (!userError && user) {
          userData = user;
        } else if (userError) {
          console.warn('公開詳情略過 users 列（RLS 或缺失），仍顯示工匠主檔:', userError.message);
        }
      }

      // 步骤3: 如果 craftsman 是 Premium，获取 premiumPlanType
      // 注意：Premium 功能主要针对公司，但 craftsman 也可能有 Premium 状态
      let premiumPlanType: 'monthly' | 'quarterly' | 'yearly' | undefined = undefined;
      if (craftsmanData.is_premium && craftsmanData.premium_until) {
        // 检查是否有关联的公司，如果有，获取公司的 premiumPlanType
        // 否则，craftsman 的 Premium 状态可能来自个人 Premium（如果有的话）
        // 目前 Premium 功能主要针对公司，所以这里暂时不获取
        // 如果需要支持个人 Premium，可以在这里添加逻辑
      }

      // 步骤4: 動態統計已完成案件（公開詳情頁與後台儀表板一致）
      const craftsmanId = String(craftsmanData.id);
      const completedJobsCount = await this.countCompletedJobs(craftsmanId);

      const workingHours = this.parseWorkingHours(craftsmanData.working_hours as Parameters<
        typeof CraftsmanService.parseWorkingHours
      >[0]);
      const availability = this.computeAvailability(craftsmanData);

      return {
        id: craftsmanId,
        userId: userId || '',
        name: (userData?.name && String(userData.name).trim()) || '專業工匠',
        profileImage: this.convertProfileImageUrl(userData?.profile_image),
        rating: Number(craftsmanData.rating) || 0,
        numReviews: Number(craftsmanData.total_reviews) || 0,
        specialties: (craftsmanData.specialties as number[]) || [],
        serviceAreas:
          (craftsmanData.service_areas as string[]) ||
          (craftsmanData.serviceAreas as string[]) ||
          [],
        location: this.parseCraftsmanLocation(craftsmanData.location),
        ...availability,
        responseTime: '5 min',
        hourlyRate: Number(craftsmanData.hourly_rate) || 0,
        isPremium: Boolean(craftsmanData.is_premium),
        premiumUntil: craftsmanData.premium_until
          ? new Date(String(craftsmanData.premium_until))
          : undefined,
        premiumPlanType,
        totalEarnings: Number(craftsmanData.total_earnings) || 0,
        completedJobs: completedJobsCount,
        accountBalance: Number(craftsmanData.account_balance) || 0,
        badgeLevel: CraftsmanService.effectiveBadgeLevelFromRow(craftsmanData),
        ...(workingHours && Object.keys(workingHours).length > 0 ? { workingHours } : {}),
      };
    } catch (error) {
      console.error('Error fetching craftsman:', error);
      return null;
    }
  }

  // 創建工匠資料
  static async createCraftsman(craftsmanData: any): Promise<{ success: boolean; error: string | null }> {
    try {
      const { data, error } = await supabase
        .from('craftsmen')
        .insert({
          user_id: craftsmanData.userId,
          company_name: craftsmanData.companyName,
          tax_id: craftsmanData.taxId || null,
          specialties: craftsmanData.specialties,
          service_areas: craftsmanData.serviceAreas,
          description: craftsmanData.description,
          certificates: craftsmanData.certificates,
          bank_account: craftsmanData.bankAccount,
          hourly_rate: craftsmanData.hourlyRate,
          status: 'active',
          created_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Error creating craftsman:', error);
      return { success: false, error: '創建工匠資料失敗' };
    }
  }

  // 根據用戶ID查詢工匠
  static async getCraftsmanByUserId(userId: string): Promise<Craftsman | null> {
    try {
      console.log('查詢工匠資料，用戶ID:', userId);
      
      // 如果 userId 不是有效的 UUID（例如 temp-user-id），跳過查詢
      if (!isValidUUID(userId)) {
        console.log('用戶ID不是有效的UUID，跳過查詢');
        return null;
      }
      
      const { data: craftsmanData, error: craftsmanError } = await supabase
        .from('craftsmen')
        .select('*')
        .eq('user_id', userId);

      console.log('工匠資料查詢結果:', { craftsmanData, craftsmanError });

      if (craftsmanError) {
        console.error('Error fetching craftsman data:', craftsmanError);
        return null;
      }

      if (!craftsmanData || craftsmanData.length === 0) {
        console.log('用戶沒有工匠記錄');
        return null;
      }

      const craftsman = craftsmanData[0];

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('name, email, phone, profile_image')
        .eq('id', userId)
        .single();

      console.log('用戶資料查詢結果:', { userData, userError });

      if (userError || !userData) {
        console.error('Error fetching user data:', userError);
        return null;
      }

      return {
        id: craftsman.id, // ✅ 修正：使用 craftsmen 表的 ID（真正的 craftsman_id）
        userId: userId,   // ✅ 保留 user_id 供其他用途使用
        name: userData.name,
        profileImage: this.convertProfileImageUrl(userData.profile_image),
        rating: craftsman.rating || 0,
        numReviews: craftsman.total_reviews || 0,
        specialties: craftsman.specialties || [],
        location: craftsman.location || { lat: 0, lng: 0 },
        ...this.computeAvailability(craftsman),
        responseTime: '5 min',
        hourlyRate: craftsman.hourly_rate || 0,
        isPremium: craftsman.is_premium || false,
        premiumUntil: craftsman.premium_until,
        totalEarnings: craftsman.total_earnings || 0,
        completedJobs: craftsman.completed_jobs || 0,
        accountBalance: craftsman.account_balance || 0,
      };
    } catch (error) {
      console.error('Error in getCraftsmanByUserId:', error);
      return null;
    }
  }

  static async setWorkStatus(
    craftsmanId: string,
    isOnBreak: boolean,
    options?: { breakUntil?: Date | string | null }
  ): Promise<void> {
    try {
      const updateData: Record<string, any> = {
        is_on_break: isOnBreak,
        // 同步更新 status 字段，確保與 is_on_break 一致
        status: isOnBreak ? 'inactive' : 'active',
        updated_at: new Date().toISOString(),
      };

      if (options && 'breakUntil' in options) {
        const { breakUntil } = options;
        if (breakUntil === null || breakUntil === undefined) {
          updateData.break_until = null;
        } else {
          const breakUntilValue = typeof breakUntil === 'string' ? breakUntil : breakUntil.toISOString();
          updateData.break_until = breakUntilValue;
        }
      } else if (!isOnBreak) {
        // 清除休息截止時間以避免殘留
        updateData.break_until = null;
      }

      const { error } = await supabase
        .from('craftsmen')
        .update(updateData)
        .eq('id', craftsmanId);

      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      console.error('更新工匠工作狀態失敗:', error);
      throw error;
    }
  }

  static async autoRejectTodayJobs(
    craftsmanId: string,
    options?: { statuses?: string[] }
  ): Promise<{ rejectedCount: number; jobIds: string[] }> {
    try {
      const statuses = options?.statuses || ['pending', 'accepted'];

      const { data, error } = await supabase
        .from('service_requests')
        .select('id, title, scheduled_date, created_at, status')
        .eq('craftsman_id', craftsmanId)
        .in('status', statuses);

      if (error) {
        throw new Error(error.message);
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];

      const jobsToday = (data || []).filter((job: any) => {
        if (!job) return false;
        if (job.scheduled_date) {
          const scheduledDate = typeof job.scheduled_date === 'string'
            ? job.scheduled_date.substring(0, 10)
            : new Date(job.scheduled_date).toISOString().split('T')[0];
          if (scheduledDate === todayStr) {
            return true;
          }
        }

        if (job.created_at) {
          const createdAt = new Date(job.created_at);
          return createdAt >= today;
        }

        return false;
      });

      if (!jobsToday.length) {
        return { rejectedCount: 0, jobIds: [] };
      }

      const jobIds = jobsToday.map((job: any) => job.id);

      const { error: updateError } = await supabase
        .from('service_requests')
        .update({
          status: 'rejected',
          updated_at: new Date().toISOString(),
        })
        .in('id', jobIds);

      if (updateError) {
        throw new Error(updateError.message);
      }

      // TODO: 可在此處理通知或訊息推播

      return {
        rejectedCount: jobIds.length,
        jobIds,
      };
    } catch (error) {
      console.error('自動拒絕當日案件失敗:', error);
      throw error;
    }
  }

  // 獲取工匠儀表板統計數據
  static async getDashboardStats(craftsmanId: string): Promise<{
    totalEarnings: number;
    completedJobs: number;
    pendingJobs: number;
    averageRating: number;
    totalReviews: number;
    thisMonthEarnings: number;
    totalCustomers: number;
    responseRate: number;
    badgeLevel: 'none' | 'excellent' | 'good' | 'warning' | 'risk';
    badgeTitle: string;
    badgeMessage: string;
    controlStatus: 'normal' | 'monitor' | 'limited';
    controlMessage: string;
    // ✅ 低优先级：详细统计报表增强
    exposureScore?: number;
    exposureMultiplier?: number;
    improvementPlan?: {
      hasActivePlan: boolean;
      status?: 'active' | 'completed' | 'cancelled';
      progress?: number; // 完成进度 0-100
    };
    // 趨勢數據
    trends: {
      rating: { value: number; percentage: number; direction: 'up' | 'down' | 'stable'; displayText?: string };
      completedJobs: { value: number; percentage: number; direction: 'up' | 'down' | 'stable'; displayText?: string };
      completionRate: { value: number; percentage: number; direction: 'up' | 'down' | 'stable'; displayText?: string };
      earnings: { value: number; percentage: number; direction: 'up' | 'down' | 'stable'; displayText?: string };
    };
    /** 已完成／已取消／已拒絕案件之整體完成占比；無終結案件時為 null */
    overallCompletionRate: number | null;
  }> {
    try {
      // 與 commission-stats 一致：優先 payments.craftsman_earnings，無則依 actual_price 推算
      // 1. 查询 service_requests 表，获取 actual_price
      const { data: serviceRequests, error: srError } = await supabase
        .from('service_requests')
        .select('id, actual_price, estimated_price, urgency, category_id, status, completed_at, created_at, pricing_mode')
        .eq('craftsman_id', craftsmanId)
        .in('status', ['completed', 'in_progress']);

      if (srError) {
        console.error('❌ 獲取服務請求數據失敗:', srError);
      }

      // 2. 获取工匠信息用于计算佣金（直接查询以获取 is_premium、badge 覆寫）
      const { data: craftsmanData } = await supabase
        .from('craftsmen')
        .select('is_premium, badge_level_override')
        .eq('id', craftsmanId)
        .single();
      
      const isPremium = craftsmanData?.is_premium || false;

      // 3. 獲取付款記錄（包含 request_id）
      const { data: earningsData, error: earningsError } = await supabase
        .from('payments')
        .select('request_id, amount, paid_amount, deposit_amount, remaining_amount, created_at, paid_at, craftsman_earnings, status, payment_type')
        .eq('craftsman_id', craftsmanId)
        .in('status', ['completed', 'paid', 'approved']);

      if (earningsError) {
        console.error('❌ 獲取收入數據失敗:', earningsError);
      }

      type EarningsRow = {
        request_id?: string | null;
        amount: number;
        paid_amount?: number | null;
        deposit_amount?: number | null;
        remaining_amount?: number | null;
        created_at: string;
        paid_at?: string | null;
        craftsman_earnings?: number | null;
        status: string;
        payment_type?: string | null;
      };

      const rows: EarningsRow[] = (earningsData as EarningsRow[]) || [];

      // 按 request_id 分组付款记录
      const paymentsByRequest = rows.reduce((acc, row) => {
        const requestId = row.request_id;
        if (!requestId) return acc;
        if (!acc[requestId]) {
          acc[requestId] = [];
        }
        acc[requestId].push(row);
        return acc;
      }, {} as Record<string, EarningsRow[]>);

      let totalEarnings = 0;
      const earningsBreakdown: Array<{
        requestId: string;
        actualPrice?: number;
        calculatedEarnings: number;
        method: string;
      }> = [];

      for (const [requestId, paymentRows] of Object.entries(paymentsByRequest)) {
        const serviceRequest = serviceRequests?.find(sr => sr.id === requestId);
        const requestEarnings = CraftsmanService.resolveRequestCraftsmanEarnings(
          paymentRows,
          serviceRequest,
          isPremium
        );
        totalEarnings += requestEarnings;
        const fromDb = CraftsmanService.sumPaymentsCraftsmanEarnings(paymentRows);
        earningsBreakdown.push({
          requestId,
          actualPrice:
            serviceRequest?.actual_price != null
              ? Number(serviceRequest.actual_price)
              : undefined,
          calculatedEarnings: requestEarnings,
          method: fromDb > 0 ? 'payments_craftsman_earnings' : 'actual_price_fallback',
        });
      }

      // ✅ 添加調試日志
      console.log('💰 總收入計算詳情:', {
        craftsmanId,
        totalRecords: rows.length,
        uniqueRequests: Object.keys(paymentsByRequest).length,
        totalEarnings,
        breakdown: earningsBreakdown
      });

      // 計算本月和上月的時間範圍
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      startOfMonth.setHours(0, 0, 0, 0);
      
      const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      startOfLastMonth.setHours(0, 0, 0, 0);
      const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999);

      let thisMonthEarnings = 0;
      const thisMonthBreakdown: Array<{
        requestId: string;
        actualPrice?: number;
        calculatedEarnings: number;
        method: string;
      }> = [];

      for (const [requestId, paymentRows] of Object.entries(paymentsByRequest)) {
        const serviceRequest = serviceRequests?.find(sr => sr.id === requestId);

        const isThisMonth =
          serviceRequest &&
          ((serviceRequest.completed_at &&
            new Date(serviceRequest.completed_at) >= startOfMonth) ||
            (!serviceRequest.completed_at &&
              new Date(serviceRequest.created_at) >= startOfMonth));

        if (!isThisMonth) continue;

        const requestEarnings = CraftsmanService.resolveRequestCraftsmanEarnings(
          paymentRows,
          serviceRequest,
          isPremium
        );
        thisMonthEarnings += requestEarnings;
        const fromDb = CraftsmanService.sumPaymentsCraftsmanEarnings(paymentRows);
        thisMonthBreakdown.push({
          requestId,
          actualPrice:
            serviceRequest?.actual_price != null
              ? Number(serviceRequest.actual_price)
              : undefined,
          calculatedEarnings: requestEarnings,
          method: fromDb > 0 ? 'payments_craftsman_earnings' : 'actual_price_fallback',
        });
      }

      // ✅ 添加調試日志
      console.log('💰 本月收入計算詳情:', {
        craftsmanId,
        startOfMonth: startOfMonth.toISOString(),
        thisMonthEarnings,
        breakdown: thisMonthBreakdown
      });

      // 獲取上月收入（使用相同的逻辑）
      let lastMonthEarnings = 0;
      for (const [requestId, paymentRows] of Object.entries(paymentsByRequest)) {
        const serviceRequest = serviceRequests?.find(sr => sr.id === requestId);
        
        const paymentDates = paymentRows
          .map(r => r.paid_at ? new Date(r.paid_at) : new Date(r.created_at))
          .filter(d => !isNaN(d.getTime()));
        const serviceDate = serviceRequest?.completed_at 
          ? new Date(serviceRequest.completed_at)
          : (serviceRequest?.created_at ? new Date(serviceRequest.created_at) : null);
        
        const relevantDate = paymentDates.length > 0 
          ? paymentDates[0]
          : (serviceDate || null);
        
        if (!relevantDate || relevantDate < startOfLastMonth || relevantDate > endOfLastMonth) {
          continue;
        }
        
        lastMonthEarnings += CraftsmanService.resolveRequestCraftsmanEarnings(
          paymentRows,
          serviceRequest,
          isPremium
        );
      }

      // 獲取已完成案件數（包含創建時間）
      const { data: completedJobsData } = await supabase
        .from('service_requests')
        .select('id, completed_at, created_at')
        .eq('craftsman_id', craftsmanId)
        .eq('status', 'completed');

      const completedJobs = completedJobsData?.length || 0;

      // 計算本月完成案件數（使用 completed_at，如果沒有則使用 created_at）
      const thisMonthCompletedJobs = completedJobsData?.filter(job => {
        const completionDate = job.completed_at 
          ? new Date(job.completed_at)
          : (job.created_at ? new Date(job.created_at) : null);
        return completionDate && completionDate >= startOfMonth;
      }).length || 0;

      // 計算上月完成案件數
      const lastMonthCompletedJobs = completedJobsData?.filter(job => {
        const completionDate = job.completed_at 
          ? new Date(job.completed_at)
          : (job.created_at ? new Date(job.created_at) : null);
        return completionDate && completionDate >= startOfLastMonth && completionDate <= endOfLastMonth;
      }).length || 0;

      // 獲取待處理案件數
      const { data: pendingJobsData } = await supabase
        .from('service_requests')
        .select('id')
        .eq('craftsman_id', craftsmanId)
        .in('status', ['pending', 'accepted', 'in_progress']);

      const pendingJobs = pendingJobsData?.length || 0;

      // 獲取評分和評價（包含創建時間）
      const { data: reviewsData } = await supabase
        .from('reviews')
        .select('rating, created_at')
        .eq('craftsman_id', craftsmanId);

      const totalReviews = reviewsData?.length || 0;
      const averageRating = totalReviews > 0 
        ? reviewsData?.reduce((sum, review) => sum + review.rating, 0) / totalReviews 
        : 0;

      // 計算本月平均評分
      const thisMonthReviews = reviewsData?.filter(review => {
        const reviewDate = review.created_at ? new Date(review.created_at) : null;
        return reviewDate && reviewDate >= startOfMonth;
      }) || [];
      const thisMonthAverageRating = thisMonthReviews.length > 0
        ? thisMonthReviews.reduce((sum, review) => sum + review.rating, 0) / thisMonthReviews.length
        : 0;

      // 計算上月平均評分
      const lastMonthReviews = reviewsData?.filter(review => {
        const reviewDate = review.created_at ? new Date(review.created_at) : null;
        return reviewDate && reviewDate >= startOfLastMonth && reviewDate <= endOfLastMonth;
      }) || [];
      const lastMonthAverageRating = lastMonthReviews.length > 0
        ? lastMonthReviews.reduce((sum, review) => sum + review.rating, 0) / lastMonthReviews.length
        : 0;

      // 獲取總客戶數
      const { data: customersData } = await supabase
        .from('service_requests')
        .select('user_id')
        .eq('craftsman_id', craftsmanId)
        .not('user_id', 'is', null);

      const uniqueCustomers = new Set(customersData?.map(req => req.user_id) || []);
      const totalCustomers = uniqueCustomers.size;

      // 計算回應率（假設為 95%）
      const responseRate = 95;

      // 計算完成率趨勢（需要獲取本月和上月的總案件數和完成案件數）
      const { data: allJobsData } = await supabase
        .from('service_requests')
        .select('id, status, completed_at, created_at')
        .eq('craftsman_id', craftsmanId)
        .in('status', ['completed', 'cancelled', 'rejected']);

      const terminalJobsList = allJobsData ?? [];
      const overallTerminalCount = terminalJobsList.length;
      const overallCompletedInTerminal = terminalJobsList.filter((j) => j.status === 'completed').length;
      const overallCompletionRate =
        overallTerminalCount > 0
          ? Math.round((overallCompletedInTerminal / overallTerminalCount) * 100)
          : null;

      // 計算本月完成率
      const thisMonthAllJobs = allJobsData?.filter(job => {
        const jobDate = job.completed_at 
          ? new Date(job.completed_at)
          : (job.created_at ? new Date(job.created_at) : null);
        return jobDate && jobDate >= startOfMonth;
      }) || [];
      const thisMonthCompleted = thisMonthAllJobs.filter(job => job.status === 'completed').length;
      const thisMonthCompletionRate = thisMonthAllJobs.length > 0
        ? Math.round((thisMonthCompleted / thisMonthAllJobs.length) * 100)
        : 0;

      // 計算上月完成率
      const lastMonthAllJobs = allJobsData?.filter(job => {
        const jobDate = job.completed_at 
          ? new Date(job.completed_at)
          : (job.created_at ? new Date(job.created_at) : null);
        return jobDate && jobDate >= startOfLastMonth && jobDate <= endOfLastMonth;
      }) || [];
      const lastMonthCompleted = lastMonthAllJobs.filter(job => job.status === 'completed').length;
      const lastMonthCompletionRate = lastMonthAllJobs.length > 0
        ? Math.round((lastMonthCompleted / lastMonthAllJobs.length) * 100)
        : 0;

      const badgeLevel = resolveCraftsmanBadgeLevel(
        averageRating,
        totalReviews,
        craftsmanData?.badge_level_override ?? null
      );
      let badgeTitle = '尚未累積評價';
      let badgeMessage = '完成服務後邀請客戶留下評價，口碑越高曝光越高。';
      let controlStatus: 'normal' | 'monitor' | 'limited' = 'normal';
      let controlMessage = '狀態良好，維持穩定服務品質即可。';

      if (badgeLevel === 'excellent') {
        badgeTitle = '金牌工匠候選';
        badgeMessage = '符合金牌徽章申請條件，可享曝光優先權。請保持快速回覆與穩定品質。';
      } else if (badgeLevel === 'good') {
        badgeTitle = '優良口碑';
        badgeMessage = '口碑表現良好，建議持續主動回覆每一則評價，建立更扎實的信任。';
      } else if (badgeLevel === 'warning') {
        badgeTitle = '需要改善';
        badgeMessage = '建議檢視近期低分評價，提供補救方案或優惠，避免進一步降評。';
        controlStatus = 'monitor';
        controlMessage = '平台已開始密切觀察，若持續低分可能降低曝光。';
      } else if (badgeLevel === 'risk') {
        badgeTitle = '風險警示';
        badgeMessage = '近期評價偏低，建議暫停接案或聯絡客服協助排查，避免帳號受限。';
        controlStatus = 'limited';
        controlMessage = '曝光將降低，請先與客服溝通改善方案後再恢復正常接案。';
      }

      // 計算趨勢數據（優化極端情況處理）
      const calculateTrend = (
        thisMonth: number,
        lastMonth: number,
        isPercentage: boolean = false
      ): { value: number; percentage: number; direction: 'up' | 'down' | 'stable'; displayText?: string } => {
        const diff = thisMonth - lastMonth;
        
        // 處理極端情況
        // 情況1：上月為0，本月有數據 -> 新增
        if (lastMonth === 0 && thisMonth > 0) {
          return {
            value: thisMonth,
            percentage: 100,
            direction: 'up',
            displayText: `新增 ${isPercentage ? `${thisMonth}%` : thisMonth}`
          };
        }
        
        // 情況2：本月為0，上月有數據 -> 無數據
        if (thisMonth === 0 && lastMonth > 0) {
          return {
            value: -lastMonth,
            percentage: -100,
            direction: 'down',
            displayText: '本月無數據'
          };
        }
        
        // 情況3：兩者都為0 -> 穩定
        if (thisMonth === 0 && lastMonth === 0) {
          return {
            value: 0,
            percentage: 0,
            direction: 'stable',
            displayText: '無變化'
          };
        }
        
        // 情況4：數據不足（本月或上月數據太少，百分比變化過於極端）
        // 如果百分比變化超過 100%，且數據量很小，顯示為"數據不足"
        const percentage = lastMonth > 0 
          ? Math.round((diff / lastMonth) * 100 * 10) / 10
          : 0;
        
        // 對於完成率，如果變化超過50%，可能是數據不足
        if (isPercentage && Math.abs(percentage) > 50 && (thisMonth < 5 || lastMonth < 5)) {
          return {
            value: diff,
            percentage,
            direction: Math.abs(diff) < 0.01 ? 'stable' : (diff > 0 ? 'up' : 'down'),
            displayText: '數據不足'
          };
        }
        
        // 正常計算
        let direction: 'up' | 'down' | 'stable' = 'stable';
        if (Math.abs(diff) < 0.01) {
          direction = 'stable';
        } else if (diff > 0) {
          direction = 'up';
        } else {
          direction = 'down';
        }

        return {
          value: isPercentage ? diff : Math.round(diff * 10) / 10,
          percentage,
          direction
        };
      };

      const trends = {
        rating: calculateTrend(thisMonthAverageRating, lastMonthAverageRating),
        completedJobs: calculateTrend(thisMonthCompletedJobs, lastMonthCompletedJobs),
        completionRate: calculateTrend(thisMonthCompletionRate, lastMonthCompletionRate, true),
        earnings: calculateTrend(thisMonthEarnings, lastMonthEarnings)
      };

      if (overallTerminalCount === 0) {
        trends.completionRate = {
          value: 0,
          percentage: 0,
          direction: 'stable',
          displayText: '尚無結案資料'
        };
      }

      // ✅ 低优先级：计算曝光分数和倍数
      let exposureScore = 0;
      let exposureMultiplier = 1.0;
      try {
        const { ExposureService } = await import('./exposureService');
        
        // ✅ 从 service_requests 表计算取消的工单数（因为 cancelled_jobs 列不存在）
        const { data: cancelledJobsData } = await supabase
          .from('service_requests')
          .select('id', { count: 'exact' })
          .eq('craftsman_id', craftsmanId)
          .eq('status', 'cancelled');
        
        const cancelledJobs = cancelledJobsData?.length || 0;
        
        // ✅ 计算平均响应时间（分钟）
        let avgResponseTime: number | undefined = undefined;
        try {
          const { data: responseTimeData } = await supabase
        .from('service_requests')
            .select('created_at, accepted_at')
        .eq('craftsman_id', craftsmanId)
            .not('accepted_at', 'is', null)
            .not('created_at', 'is', null);
          
          if (responseTimeData && responseTimeData.length > 0) {
            const responseTimes = responseTimeData
              .map(job => {
                const created = new Date(job.created_at);
                const accepted = new Date(job.accepted_at);
                return (accepted.getTime() - created.getTime()) / (1000 * 60); // 转换为分钟
              })
              .filter(time => time > 0 && time < 1440); // 过滤异常值（0-24小时）
            
            if (responseTimes.length > 0) {
              avgResponseTime = responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length;
            }
          }
        } catch (error) {
          console.error('计算平均响应时间失败:', error);
        }

        // ✅ 计算最近30天的5星评价数
        let recentGoodReviews = 0;
        try {
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          
          const { data: recentReviews } = await supabase
            .from('reviews')
            .select('rating, created_at')
            .eq('craftsman_id', craftsmanId)
            .eq('rating', 5)
            .gte('created_at', thirtyDaysAgo.toISOString());
          
          recentGoodReviews = recentReviews?.length || 0;
        } catch (error) {
          console.error('计算近期好评数失败:', error);
        }

        exposureScore = ExposureService.calculateExposureScore({
          rating: averageRating,
          numReviews: totalReviews,
          isPremium: isPremium,
          completedJobs,
          cancelledJobs,
          badgeLevel,
          avgResponseTime,  // ✅ 添加
          recentGoodReviews, // ✅ 添加
        } as any);
        exposureMultiplier = ExposureService.getExposureMultiplier(badgeLevel);
      } catch (error) {
        console.error('计算曝光分数失败:', error);
      }

      // ✅ 低优先级：获取改善计划状态
      let improvementPlan: {
        hasActivePlan: boolean;
        status?: 'active' | 'completed' | 'cancelled';
        progress?: number;
      } = { hasActivePlan: false };
      try {
        const { ImprovementPlanService } = await import('./improvementPlanService');
        const { plan } = await ImprovementPlanService.getCraftsmanPlan(craftsmanId);
        if (plan) {
          const progress = plan.requiredActions.length > 0
            ? Math.round((plan.completedActions.length / plan.requiredActions.length) * 100)
            : 0;
          improvementPlan = {
            hasActivePlan: true,
            status: plan.status,
            progress,
          };
        }
      } catch (error) {
        console.error('获取改善计划失败:', error);
      }

      return {
        totalEarnings,
        completedJobs,
        pendingJobs,
        averageRating: Math.round(averageRating * 10) / 10,
        totalReviews,
        thisMonthEarnings,
        totalCustomers,
        responseRate,
        badgeLevel,
        badgeTitle,
        badgeMessage,
        controlStatus,
        controlMessage,
        exposureScore,
        exposureMultiplier,
        improvementPlan,
        trends,
        overallCompletionRate
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return {
        totalEarnings: 0,
        completedJobs: 0,
        pendingJobs: 0,
        averageRating: 0,
        totalReviews: 0,
        thisMonthEarnings: 0,
        totalCustomers: 0,
        responseRate: 0,
        badgeLevel: 'none',
        badgeTitle: '尚未累積評價',
        badgeMessage: '完成服務後邀請客戶留下評價，口碑越高曝光越高。',
        controlStatus: 'normal',
        controlMessage: '保持穩定服務品質，持續累積好評。',
        exposureScore: 0,
        exposureMultiplier: 1.0,
        improvementPlan: { hasActivePlan: false },
        trends: {
          rating: { value: 0, percentage: 0, direction: 'stable' },
          completedJobs: { value: 0, percentage: 0, direction: 'stable' },
          completionRate: {
            value: 0,
            percentage: 0,
            direction: 'stable',
            displayText: '尚無結案資料'
          },
          earnings: { value: 0, percentage: 0, direction: 'stable' }
        },
        overallCompletionRate: null
      };
    }
  }

  // 獲取最近案件
  static async getRecentJobs(craftsmanId: string, limit: number = 5): Promise<any[]> {
    try {
      console.log('🔍 查詢工匠的最近案件, craftsman_id:', craftsmanId);
      
      // 先查询服务请求（避免多重关系错误）
      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          id,
          title,
          description,
          status,
          estimated_price,
          actual_price,
          completed_at,
          pricing_mode,
          fixed_total_price,
          created_at,
          user_id
        `)
        .eq('craftsman_id', craftsmanId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('❌ Error fetching recent jobs:', error);
        return [];
      }

      console.log('✅ 查询到服务请求:', data?.length || 0, '条');

      // 如果没有数据，直接返回空数组
      if (!data || data.length === 0) {
        return [];
      }

      // 获取所有客户的 user_id
      const userIds = [...new Set(data.map(job => job.user_id))];
      console.log('📋 获取客户信息, user_ids:', userIds);

      // 分别查询客户信息
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('id, name, email, phone')
        .in('id', userIds);

      if (usersError) {
        console.error('❌ Error fetching users:', usersError);
      }

      console.log('✅ 查询到客户信息:', users?.length || 0, '个');

      const userMap = new Map(users?.map(u => [u.id, u]) || []);

      const jobIds = data.map(job => job.id);
      const { data: craftsmanRow } = await supabase
        .from('craftsmen')
        .select('is_premium')
        .eq('id', craftsmanId)
        .single();
      const isPremium = craftsmanRow?.is_premium || false;

      const { data: jobPayments } = await supabase
        .from('payments')
        .select('request_id, craftsman_earnings')
        .eq('craftsman_id', craftsmanId)
        .in('request_id', jobIds)
        .in('status', ['completed', 'paid', 'approved']);

      const paymentsByRequest = (jobPayments || []).reduce(
        (acc, p) => {
          if (!p.request_id) return acc;
          if (!acc[p.request_id]) acc[p.request_id] = [];
          acc[p.request_id].push(p);
          return acc;
        },
        {} as Record<string, typeof jobPayments>
      );

      return data.map(job => {
        const user = userMap.get(job.user_id);
        const paymentRows = paymentsByRequest[job.id] || [];
        const craftsmanEarnings = CraftsmanService.resolveRequestCraftsmanEarnings(
          paymentRows,
          job,
          isPremium
        );
        const contractPrice =
          job.actual_price && Number(job.actual_price) > 0
            ? Number(job.actual_price)
            : Number(job.estimated_price || 0);

        return {
          id: job.id,
          title: job.title,
          customer: user?.name || 'Unknown',
          customerEmail: user?.email || '',
          customerPhone: user?.phone || '',
          amount: craftsmanEarnings > 0 ? craftsmanEarnings : contractPrice,
          craftsmanEarnings,
          contractPrice,
          date: new Date(job.created_at).toLocaleDateString('zh-TW'),
          status: job.status,
          description: job.description,
        };
      });
    } catch (error) {
      console.error('❌ Exception in getRecentJobs:', error);
      return [];
    }
  }

  // 獲取收入趨勢數據
  static async getEarningsTrend(craftsmanId: string, months: number = 6): Promise<{
    labels: string[];
    data: number[];
  }> {
    try {
      const { data: serviceRequests } = await supabase
        .from('service_requests')
        .select('id, actual_price, estimated_price, urgency, category_id, status, completed_at, created_at, pricing_mode')
        .eq('craftsman_id', craftsmanId)
        .in('status', ['completed', 'in_progress']);

      // 获取工匠信息（用于计算佣金）
      const { data: craftsmanData } = await supabase
        .from('craftsmen')
        .select('is_premium')
        .eq('id', craftsmanId)
        .single();
      
      const isPremium = craftsmanData?.is_premium || false;

      // 获取所有付款记录（包含 request_id）
      const { data: allPayments } = await supabase
        .from('payments')
        .select('request_id, craftsman_earnings, created_at, paid_at')
        .eq('craftsman_id', craftsmanId)
        .in('status', ['completed', 'paid', 'approved']);

      // 按 request_id 分组付款记录
      const paymentsByRequest = (allPayments || []).reduce((acc, payment) => {
        const requestId = payment.request_id;
        if (!requestId) return acc;
        if (!acc[requestId]) {
          acc[requestId] = [];
        }
        acc[requestId].push(payment);
        return acc;
      }, {} as Record<string, typeof allPayments>);

      const labels = [];
      const data = [];
      const now = new Date();

      for (let i = months - 1; i >= 0; i--) {
        const startOfMonth = new Date(now.getFullYear(), now.getMonth() - i, 1);
        startOfMonth.setHours(0, 0, 0, 0);
        
        // ✅ 修复：对于当前月（i === 0），使用当前时间；对于历史月份，使用当月最后一天
        let endOfMonth: Date;
        if (i === 0) {
          // 当前月：使用当前时间
          endOfMonth = new Date(now);
        } else {
          // 历史月份：使用当月最后一天的 23:59:59.999
          endOfMonth = new Date(now.getFullYear(), now.getMonth() - i + 1, 0, 23, 59, 59, 999);
        }

        const monthLabel = startOfMonth.toLocaleDateString('zh-TW', { month: 'short' });
        labels.push(monthLabel);

        let monthlyEarnings = 0;

        for (const [requestId, paymentRows] of Object.entries(paymentsByRequest)) {
          const serviceRequest = serviceRequests?.find(sr => sr.id === requestId);

          const paymentDates = (paymentRows || [])
            .map(r => r.paid_at ? new Date(r.paid_at) : new Date(r.created_at))
            .filter(d => !isNaN(d.getTime()));
          const serviceDate = serviceRequest?.completed_at
            ? new Date(serviceRequest.completed_at)
            : serviceRequest?.created_at
              ? new Date(serviceRequest.created_at)
              : null;

          const relevantDate =
            paymentDates.length > 0 ? paymentDates[0] : serviceDate || null;

          if (!relevantDate || relevantDate < startOfMonth || relevantDate > endOfMonth) {
            continue;
          }

          monthlyEarnings += CraftsmanService.resolveRequestCraftsmanEarnings(
            paymentRows || [],
            serviceRequest,
            isPremium
          );
        }
        
        data.push(monthlyEarnings);
      }

      return { labels, data };
    } catch (error) {
      console.error('Error fetching earnings trend:', error);
      return {
        labels: ['1月', '2月', '3月', '4月', '5月', '6月'],
        data: new Array(6).fill(0)
      };
    }
  }

  // 獲取工匠績效數據
  static async getPerformanceMetrics(craftsmanId: string): Promise<{
    completionRate: number;
    averageResponseTime: number;
    customerSatisfaction: number;
    repeatCustomers: number;
  }> {
    try {
      // 計算完成率
      const { data: allJobs } = await supabase
        .from('service_requests')
        .select('status')
        .eq('craftsman_id', craftsmanId);

      const totalJobs = allJobs?.length || 0;
      const completedJobs = allJobs?.filter(job => job.status === 'completed').length || 0;
      const completionRate = totalJobs > 0 ? (completedJobs / totalJobs) * 100 : 0;

      // 平均回應時間（分鐘）- 假設數據
      const averageResponseTime = 15;

      // 客戶滿意度（基於評分）
      const { data: reviews } = await supabase
        .from('reviews')
        .select('rating')
        .eq('craftsman_id', craftsmanId);

      const customerSatisfaction = reviews?.length > 0 
        ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length) * 20 
        : 0;

      // 重複客戶數
      const { data: customerData } = await supabase
        .from('service_requests')
        .select('user_id')
        .eq('craftsman_id', craftsmanId);

      const customerCounts = {};
      customerData?.forEach(job => {
        customerCounts[job.user_id] = (customerCounts[job.user_id] || 0) + 1;
      });

      const repeatCustomers = Object.values(customerCounts).filter(count => count > 1).length;

      return {
        completionRate: Math.round(completionRate),
        averageResponseTime,
        customerSatisfaction: Math.round(customerSatisfaction),
        repeatCustomers
      };
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
      return {
        completionRate: 0,
        averageResponseTime: 0,
        customerSatisfaction: 0,
        repeatCustomers: 0
      };
    }
  }

  // 獲取工匠完整個人資料
  static async getCraftsmanProfile(userId: string): Promise<{
    id: string;
    companyName: string;
    taxId: string;
    description: string;
    phone: string;
    email: string;
    address: string;
    specialties: string[];
    serviceAreas: string[];
    hourlyRate: number;
    rating: number;
    totalReviews: number;
    completedJobs: number;
    totalEarnings: number;
    status: string;
    isOnBreak: boolean;
    certificates: any[];
    profileImage?: string;
    workingHours?: any;
    /** 由 service_requests 終結案件計算；無資料時為 null */
    completionRate?: number | null;
    responseTime?: string;
    bankAccount: {
      bankCode: string;
      accountNumber: string;
      accountName: string;
    };
    trends?: {
      rating: { value: number; percentage: number; direction: 'up' | 'down' | 'stable' };
      completedJobs: { value: number; percentage: number; direction: 'up' | 'down' | 'stable' };
      completionRate: { value: number; percentage: number; direction: 'up' | 'down' | 'stable' };
      earnings: { value: number; percentage: number; direction: 'up' | 'down' | 'stable' };
    };
    /** 後台「文件狀態」，供工匠端顯示補件說明 */
    documentsStatus?: string | null;
    /** 後台審核備註／退件原因（唯讀） */
    reviewNotes?: string | null;
  } | null> {
    try {
      console.log('獲取工匠個人資料，用戶ID:', userId);
      
      // 修改查詢，明確指定關聯
      const { data, error } = await supabase
        .from('craftsmen')
        .select(`
          *,
          users!craftsmen_user_id_fkey(name, email, phone, profile_image)
        `)
        .eq('user_id', userId)
        .single();

      console.log('查詢結果:', { data, error });

      if (error || !data) {
        console.error('Error fetching craftsman profile:', error);
        return null;
      }

      // 獲取統計數據（動態計算完成案件數）
      const stats = await this.getDashboardStats(data.id);
      console.log('統計數據:', stats);

      console.log('原始證書數據:', data.certificates);
      console.log('證書數據類型:', typeof data.certificates);
      
      // 確保證書數據是正確的格式
      let certificates = [];
      if (data.certificates) {
        if (typeof data.certificates === 'string') {
          try {
            certificates = JSON.parse(data.certificates);
          } catch (e) {
            console.error('解析證書JSON失敗:', e);
            certificates = [];
          }
        } else if (Array.isArray(data.certificates)) {
          // 檢查數組中的每個元素是否為字符串，如果是則解析
          certificates = data.certificates.map(cert => {
            if (typeof cert === 'string') {
              try {
                return JSON.parse(cert);
              } catch (e) {
                console.error('解析證書項目失敗:', e, cert);
                return null;
              }
            } else if (typeof cert === 'object' && cert !== null) {
              return cert;
            } else {
              console.warn('無效的證書項目:', cert);
              return null;
            }
          }).filter(cert => cert !== null); // 過濾掉無效的證書
        } else {
          console.warn('證書數據格式不正確:', data.certificates);
          certificates = [];
        }
      }
      
      console.log('處理後的證書數據:', certificates);

      const isOnBreak = Boolean(data.is_on_break);

      return {
        id: data.id,
        companyName: data.company_name || '',
        taxId: data.tax_id || '',
        description: data.description || '',
        phone: data.users?.phone || '',
        email: data.users?.email || '',
        address: data.address || '',
        specialties: data.specialties || [],
        serviceAreas: data.service_areas || [],
        hourlyRate: data.hourly_rate || 0,
        rating: data.rating || 0,
        totalReviews: stats.totalReviews || data.total_reviews || 0, // 使用動態計算的評價數
        completedJobs: stats.completedJobs || 0, // 使用動態計算的完成案件數，而不是數據庫緩存字段
        totalEarnings: stats.totalEarnings || data.total_earnings || 0, // 使用動態計算的總收入
        status: data.status || 'active',
        isOnBreak,
        certificates: certificates,
        profileImage: this.convertProfileImageUrl(data.users?.profile_image),
        workingHours: data.working_hours || {},
        completionRate: stats.overallCompletionRate,
        responseTime: data.response_time || '1小時內回覆',
        bankAccount: data.bank_account || {
          bankCode: '',
          accountNumber: '',
          accountName: '',
        },
        trends: stats.trends, // 添加趨勢數據
        documentsStatus: data.documents_status ?? null,
        reviewNotes: data.review_notes ?? null,
      };
    } catch (error) {
      console.error('Error fetching craftsman profile:', error);
      return null;
    }
  }

  // 更新工匠個人資料
  static async updateCraftsmanProfile(
    userId: string,
    profileData: {
      companyName?: string;
      taxId?: string;
      description?: string;
      phone?: string;
      address?: string;
      specialties?: string[];
      serviceAreas?: string[];
      hourlyRate?: number;
      certificates?: any[];
      profileImage?: string;
      workingHours?: any;
      bankAccount?: {
        bankCode: string;
        accountNumber: string;
        accountName: string;
      };
    }
  ): Promise<{ success: boolean; error: string | null }> {
    try {
      // 首先獲取工匠記錄
      const { data: existingCraftsman, error: fetchError } = await supabase
        .from('craftsmen')
        .select('id')
        .eq('user_id', userId)
        .single();

      if (fetchError) {
        return { success: false, error: '找不到工匠資料' };
      }

      // 更新工匠資料
      const updateData: any = {
        updated_at: new Date().toISOString(),
      };

      if (profileData.companyName !== undefined) updateData.company_name = profileData.companyName;
      if (profileData.taxId !== undefined) updateData.tax_id = profileData.taxId ? profileData.taxId : null;
      if (profileData.description !== undefined) updateData.description = profileData.description;
      if (profileData.address !== undefined) updateData.address = profileData.address;
      if (profileData.specialties !== undefined) updateData.specialties = profileData.specialties;
      if (profileData.serviceAreas !== undefined) updateData.service_areas = profileData.serviceAreas;
      if (profileData.hourlyRate !== undefined) updateData.hourly_rate = profileData.hourlyRate;
      if (profileData.certificates !== undefined) updateData.certificates = profileData.certificates;
      if (profileData.bankAccount !== undefined) updateData.bank_account = profileData.bankAccount;
      
      // ✅ 如果更新了 address，同时 geocode 更新 location
      if (profileData.address !== undefined && profileData.address.trim()) {
        try {
          const { AddressService } = await import('./addressService');
          const geocodeResult = await AddressService.geocodeAddress(profileData.address);
          if (geocodeResult) {
            updateData.location = { lat: geocodeResult.lat, lng: geocodeResult.lng };
            console.log('✅ 地址已 geocode，更新 location:', updateData.location);
          } else {
            console.warn('⚠️ 地址 geocode 失败，保持原有 location');
          }
        } catch (error) {
          console.error('地址地理编码失败:', error);
          // 即使 geocode 失败，也继续更新其他字段
        }
      }
      
      // 只有當 working_hours 欄位存在時才更新
      if (profileData.workingHours !== undefined) {
        updateData.working_hours = profileData.workingHours;
      }
      
      const { error: updateError } = await supabase
        .from('craftsmen')
        .update(updateData)
        .eq('id', existingCraftsman.id);

      if (updateError) {
        return { success: false, error: updateError.message };
      }

      // 如果更新了電話或頭像，同時更新用戶資料
      if (profileData.phone || profileData.profileImage) {
        const updateData: any = {
          updated_at: new Date().toISOString(),
        };
        
        if (profileData.phone) {
          updateData.phone = profileData.phone;
        }
        
        if (profileData.profileImage) {
          updateData.profile_image = profileData.profileImage;
        }
        
        const { error: userUpdateError } = await supabase
          .from('users')
          .update(updateData)
          .eq('id', userId);

        if (userUpdateError) {
          console.error('Error updating user data:', userUpdateError);
        }
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Error updating craftsman profile:', error);
      return { success: false, error: '更新個人資料失敗' };
    }
  }

  // 獲取工匠統計數據
  static async getCraftsmanStats(craftsmanId: string): Promise<{
    rating: number;
    totalReviews: number;
    completedJobs: number;
    totalEarnings: number;
    responseRate: number;
    averageResponseTime: string;
  }> {
    try {
      const stats = await this.getDashboardStats(craftsmanId);

      // 獲取評分和評價數
      const { data: reviewsData } = await supabase
        .from('reviews')
        .select('rating')
        .eq('craftsman_id', craftsmanId);

      const totalReviews = reviewsData?.length || 0;
      const averageRating = reviewsData?.length > 0 
        ? reviewsData.reduce((sum, review) => sum + review.rating, 0) / reviewsData.length 
        : 0;

      return {
        rating: averageRating,
        totalReviews,
        completedJobs: stats.completedJobs,
        totalEarnings: stats.totalEarnings,
        responseRate: stats.responseRate,
        averageResponseTime: '5 min', // 可以根據實際數據計算
      };
    } catch (error) {
      console.error('Error fetching craftsman stats:', error);
      return {
        rating: 0,
        totalReviews: 0,
        completedJobs: 0,
        totalEarnings: 0,
        responseRate: 0,
        averageResponseTime: '5 min',
      };
    }
  }

  // 獲取工匠收益明細
  static async getCraftsmanEarnings(
    craftsmanId: string, 
    timeRange: 'week' | 'month' | 'year' | 'all' = 'month'
  ): Promise<{
    earnings: Array<{
      id: string;
      date: string;
      amount: number;
      jobTitle: string;
      customerName: string;
      status: 'pending' | 'completed' | 'paid' | 'settled';
      payoutSettledAt?: string | null;
      payoutSettledNote?: string | null;
    }>;
    totalEarnings: number;
    pendingEarnings: number;
    paidEarnings: number;
  }> {
    try {
      // 計算時間範圍
      const now = new Date();
      let startDate: Date;
      
      switch (timeRange) {
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'year':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        case 'all':
          startDate = new Date(0);
          break;
        default:
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      }

      console.log('獲取收益數據，工匠ID:', craftsmanId, '時間範圍:', timeRange);
      console.log('開始日期:', startDate.toISOString());
      console.log('結束日期:', now.toISOString());

      // 獲取服務請求和支付數據
      const { data: serviceRequests, error: srError } = await supabase
        .from('service_requests')
        .select(`
          id,
          title,
          estimated_price,
          actual_price,
          status,
          urgency,
          category_id,
          created_at,
          user_id,
          customer_id,
          users!service_requests_user_id_fkey(name)
        `)
        .eq('craftsman_id', craftsmanId)
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: false });

      console.log('服務請求查詢結果:', { serviceRequests, srError });

      if (srError) {
        console.error('Error fetching service requests:', srError);
        return {
          earnings: [],
          totalEarnings: 0,
          pendingEarnings: 0,
          paidEarnings: 0
        };
      }

      // 獲取支付數據
      const { data: payments, error: pError } = await supabase
        .from('payments')
        .select('*')
        .eq('craftsman_id', craftsmanId)
        .gte('created_at', startDate.toISOString());

      console.log('支付數據查詢結果:', { payments, pError });

      if (pError) {
        console.error('Error fetching payments:', pError);
      }

      // 獲取工匠信息（用於計算佣金）
      // ✅ 修复：直接查询 is_premium，与 getDashboardStats 保持一致
      const { data: craftsmanData } = await supabase
        .from('craftsmen')
        .select('is_premium')
        .eq('id', craftsmanId)
        .single();
      
      const isPremium = craftsmanData?.is_premium || false;

      const earnings = serviceRequests?.map(request => {
        const relatedPayments = payments?.filter(p => p.request_id === request.id) || [];

        let hasPaidStatus = false;
        let hasCompletedStatus = false;
        let hasPayoutSettled = false;
        let latestPayoutSettledAt: string | null = null;
        let latestPayoutSettledNote: string | null = null;
        
        relatedPayments.forEach(payment => {
          if (payment.status === 'paid') {
            hasPaidStatus = true;
          }
          if (payment.status === 'completed') {
            hasCompletedStatus = true;
          }
          if (payment.craftsman_payout_settled_at) {
            hasPayoutSettled = true;
            const settledAt = String(payment.craftsman_payout_settled_at);
            if (!latestPayoutSettledAt || settledAt > latestPayoutSettledAt) {
              latestPayoutSettledAt = settledAt;
              latestPayoutSettledNote = payment.craftsman_payout_settled_note
                ? String(payment.craftsman_payout_settled_note)
                : null;
            }
          }
        });
        
        // ✅ 撥款顯示狀態：
        // settled（平台已標記撥款） > paid（付款已完成） > completed（待撥款） > pending（進行中）
        let status: 'pending' | 'completed' | 'paid' | 'settled' = 'pending';
        if (hasPayoutSettled) {
          status = 'settled';
        } else if (hasPaidStatus) {
          status = 'paid';
        } else if (hasCompletedStatus || request.status === 'completed') {
          status = 'completed';
        } else {
          status = 'pending';
        }

        const amount = CraftsmanService.resolveRequestCraftsmanEarnings(
          relatedPayments,
          request,
          isPremium
        );

        return {
          id: request.id,
          date: request.created_at,
          amount,
          jobTitle: request.title,
          customerName: request.users?.name || '未知客戶',
          status,
          payoutSettledAt: latestPayoutSettledAt,
          payoutSettledNote: latestPayoutSettledNote,
        };
      }) || [];

      console.log('處理後的收益數據:', earnings);

      const totalEarnings = earnings.reduce((sum, earning) => sum + earning.amount, 0);
      
      // ✅ 改進：計算待收款（從 payments 表直接查詢，更準確）
      // 待收款 = 狀態為 pending/completed/approved 但未完全付款的記錄
      const { data: pendingPaymentsData, error: pendingPaymentsError } = await supabase
        .from('payments')
        .select('craftsman_earnings, paid_amount, deposit_amount, remaining_amount, amount, created_at, request_id, status, payment_type')
        .eq('craftsman_id', craftsmanId)
        .in('status', ['pending', 'completed', 'approved', 'paid'])
        .gte('created_at', startDate.toISOString());

      if (pendingPaymentsError) {
        console.error('❌ 獲取待收款數據失敗:', pendingPaymentsError);
      }

      // 計算待收款金額
      const pendingEarnings = (pendingPaymentsData || []).reduce((sum, payment) => {
        // 計算應收金額
        let receivableAmount = 0;
        
        // 優先使用 craftsman_earnings（已扣除佣金的工匠實收）
        if (payment.craftsman_earnings != null && payment.craftsman_earnings > 0) {
          receivableAmount = payment.craftsman_earnings;
        } 
        // 如果是訂金類型，使用 deposit_amount
        else if (payment.payment_type === 'deposit' && payment.deposit_amount != null && payment.deposit_amount > 0) {
          receivableAmount = payment.deposit_amount;
        }
        // 如果是尾款類型，使用 remaining_amount
        else if (payment.payment_type === 'remaining' && payment.remaining_amount != null && payment.remaining_amount > 0) {
          receivableAmount = payment.remaining_amount;
        }
        // 最後使用 amount
        else {
          receivableAmount = Number(payment.amount || 0);
        }
        
        // 計算已付金額
        const paidAmount = Number(payment.paid_amount || 0);
        
        // 待收款 = 應收金額 - 已付金額
        // 只有當狀態不是 'paid' 或已付金額小於應收金額時，才算待收款
        if (payment.status !== 'paid' || paidAmount < receivableAmount) {
          const pendingAmount = Math.max(0, receivableAmount - paidAmount);
          return sum + pendingAmount;
        }
        
        return sum;
      }, 0);
      
      // paidEarnings: 已收款（status = 'paid'）
      const paidEarnings = earnings
        .filter(e => e.status === 'paid')
        .reduce((sum, earning) => sum + earning.amount, 0);

      console.log('💰 統計結果:', {
        totalEarnings,
        pendingEarnings,
        paidEarnings,
        earningsCount: earnings.length,
        pendingPaymentsCount: pendingPaymentsData?.length || 0,
        timeRange,
        startDate: startDate.toISOString()
      });
      
      // ✅ 添加詳細的待收款調試信息
      if (timeRange === 'week') {
        const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const weeklyPending = (pendingPaymentsData || [])
          .filter(p => {
            const paymentDate = new Date(p.created_at);
            return paymentDate >= weekStart && paymentDate <= now;
          })
          .reduce((sum, payment) => {
            let receivableAmount = 0;
            if (payment.craftsman_earnings != null && payment.craftsman_earnings > 0) {
              receivableAmount = payment.craftsman_earnings;
            } else if (payment.payment_type === 'deposit' && payment.deposit_amount != null && payment.deposit_amount > 0) {
              receivableAmount = payment.deposit_amount;
            } else if (payment.payment_type === 'remaining' && payment.remaining_amount != null && payment.remaining_amount > 0) {
              receivableAmount = payment.remaining_amount;
            } else {
              receivableAmount = Number(payment.amount || 0);
            }
            const paidAmount = Number(payment.paid_amount || 0);
            if (payment.status !== 'paid' || paidAmount < receivableAmount) {
              return sum + Math.max(0, receivableAmount - paidAmount);
            }
            return sum;
          }, 0);
        
        console.log('📅 本週待收款詳情:', {
          weekStart: weekStart.toISOString(),
          weekEnd: now.toISOString(),
          weeklyPending,
          weeklyPendingPayments: (pendingPaymentsData || []).filter(p => {
            const paymentDate = new Date(p.created_at);
            return paymentDate >= weekStart && paymentDate <= now;
          }).map(p => ({
            id: p.request_id,
            craftsman_earnings: p.craftsman_earnings,
            paid_amount: p.paid_amount,
            deposit_amount: p.deposit_amount,
            remaining_amount: p.remaining_amount,
            amount: p.amount,
            status: p.status,
            payment_type: p.payment_type,
            created_at: p.created_at
          }))
        });
      }

      return {
        earnings,
        totalEarnings,
        pendingEarnings,
        paidEarnings
      };
    } catch (error) {
      console.error('Error fetching craftsman earnings:', error);
      return {
        earnings: [],
        totalEarnings: 0,
        pendingEarnings: 0,
        paidEarnings: 0
      };
    }
  }

  // 獲取收益統計數據
  static async getEarningsStats(craftsmanId: string): Promise<{
    totalEarnings: number;
    thisMonthEarnings: number;
    thisWeekEarnings: number;
    averagePerJob: number;
    totalJobs: number;
  }> {
    try {
      // 獲取總收益和案件數
      const { data: allRequests } = await supabase
        .from('service_requests')
        .select('estimated_price, status, created_at')
        .eq('craftsman_id', craftsmanId)
        .eq('status', 'completed');

      const totalEarnings = allRequests?.reduce((sum, req) => sum + (req.estimated_price || 0), 0) || 0;
      const totalJobs = allRequests?.length || 0;
      const averagePerJob = totalJobs > 0 ? totalEarnings / totalJobs : 0;

      // 計算本月收益
      const now = new Date();
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const thisMonthRequests = allRequests?.filter(req => 
        new Date(req.created_at) >= thisMonthStart
      ) || [];
      const thisMonthEarnings = thisMonthRequests.reduce((sum, req) => sum + (req.estimated_price || 0), 0);

      // 計算本週收益
      const thisWeekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const thisWeekRequests = allRequests?.filter(req => 
        new Date(req.created_at) >= thisWeekStart
      ) || [];
      const thisWeekEarnings = thisWeekRequests.reduce((sum, req) => sum + (req.estimated_price || 0), 0);

      return {
        totalEarnings,
        thisMonthEarnings,
        thisWeekEarnings,
        averagePerJob: Math.round(averagePerJob),
        totalJobs
      };
    } catch (error) {
      console.error('Error fetching earnings stats:', error);
      return {
        totalEarnings: 0,
        thisMonthEarnings: 0,
        thisWeekEarnings: 0,
        averagePerJob: 0,
        totalJobs: 0
      };
    }
  }

  // 根据搜索词推断服务类别
  private static mapSearchTermToCategory(searchTerm: string): number | null {
    const lowerTerm = searchTerm.toLowerCase();
    
    // 服务类别关键词映射
    const categoryKeywords: Record<number, string[]> = {
      1: ['水電', '水管', '管道', '疏通', '漏水', '電路', '插座', '開關', '水龍頭', '馬桶', '排水', '水塔', '水壓'], // 水電維修
      2: ['電器', '家電', '洗衣機', '冰箱', '電視', '微波爐', '冷氣機', '除濕機', '電風扇'], // 電器維修
      3: ['冷氣', '空調', '製冷', '暖氣', '冷暖', '空調機', '冷氣機', '暖氣機'], // 空調維修
      4: ['維修', '修理', '修復', '故障', '壞掉'], // 一般維修
      5: ['油漆', '粉刷', '塗裝', '刷漆', '上漆'], // 油漆工程
      6: ['清潔', '清洗', '打掃', '掃除', '清掃'], // 清潔服務
      7: ['園藝', '造景', '綠化', '植栽', '花園'], // 園藝造景
      8: ['監視', '監控', '攝像', '攝影機', 'cctv'], // 監視系統
      9: ['木工', '裝修', '裝潢', '木作', '木製', '木地板'], // 木工裝修
      10: ['水塔', '清洗水塔', '水塔清洗'], // 水塔清洗
    };

    for (const [categoryId, keywords] of Object.entries(categoryKeywords)) {
      if (keywords.some(keyword => lowerTerm.includes(keyword))) {
        return parseInt(categoryId);
      }
    }
    
    return null;
  }

  private static craftsmanMatchesSearchTerm(
    craftsman: any,
    lowerSearchTerm: string,
    inferredCategoryId: number | null
  ): boolean {
    const name = String(craftsman.name || craftsman.user?.name || '').toLowerCase();
    const desc = String(craftsman.description || '').toLowerCase();
    const areas = ((craftsman.service_areas || craftsman.serviceAreas || []) as string[])
      .join(' ')
      .toLowerCase();
    const matchesNameOrDesc =
      name.includes(lowerSearchTerm) || desc.includes(lowerSearchTerm);
    const matchesSpecialty =
      Boolean(inferredCategoryId) &&
      Array.isArray(craftsman.specialties) &&
      craftsman.specialties.includes(inferredCategoryId);
    const matchesAreas = areas.length > 0 && areas.includes(lowerSearchTerm);
    return matchesNameOrDesc || matchesSpecialty || matchesAreas;
  }

  private static filterByServiceCity(rows: any[], serviceCity: string): any[] {
    if (!serviceCity.trim()) return rows;
    return rows.filter((craftsman: any) =>
      matchesServiceAreaList(
        craftsman.service_areas || craftsman.serviceAreas,
        serviceCity
      )
    );
  }

  // 搜索工匠
  static async searchCraftsmen(params: {
    searchTerm?: string;
    categoryId?: number;
    minRating?: number;
    maxPrice?: number;
    availability?: string;
    serviceCity?: string;
    location?: { lat: number; lng: number };
    radius?: number;
    /** 用於對照 working_hours 的參考時間（例如客戶預約時段）；未傳則用「此刻」 */
    availabilityReferenceTime?: Date;
  }): Promise<Craftsman[]> {
    try {
      const availabilityAt = params.availabilityReferenceTime;

      // 已選服務縣市時以 service_areas 為主，不走 GPS 半徑（與搜尋頁進階篩選一致）
      if (params.location && !params.serviceCity?.trim()) {
        try {
          const { data, error } = await supabase
            .rpc('get_nearby_craftsmen', {
              user_lat: params.location.lat,
              user_lng: params.location.lng,
              search_radius: params.radius || 10
            });

          if (!error && data && data.length > 0) {
            // 應用其他過濾條件
            let filteredData = data;

            // ✅ 增强搜索：根据搜索词推断类别
            let inferredCategoryId = params.categoryId;
            if (!inferredCategoryId && params.searchTerm) {
              inferredCategoryId = this.mapSearchTermToCategory(params.searchTerm);
            }

            if (params.searchTerm) {
              const lowerSearchTerm = params.searchTerm.toLowerCase();
              filteredData = filteredData.filter((craftsman: any) =>
                CraftsmanService.craftsmanMatchesSearchTerm(
                  craftsman,
                  lowerSearchTerm,
                  inferredCategoryId
                )
              );
            }

            if (params.serviceCity?.trim()) {
              filteredData = CraftsmanService.filterByServiceCity(
                filteredData,
                params.serviceCity.trim()
              );
            }

            // 使用推断的类别或明确指定的类别
            const finalCategoryId = params.categoryId || inferredCategoryId;
            if (finalCategoryId) {
              filteredData = filteredData.filter((craftsman: any) =>
                craftsman.specialties && craftsman.specialties.includes(finalCategoryId)
              );
            }

            if (params.minRating) {
              filteredData = filteredData.filter((craftsman: any) =>
                craftsman.rating >= params.minRating
              );
            }

            if (params.maxPrice) {
              filteredData = filteredData.filter((craftsman: any) =>
                craftsman.hourly_rate <= params.maxPrice
              );
            }

            if (params.availability && params.availability !== 'all') {
              filteredData = filteredData.filter((craftsman: any) =>
                this.matchesSearchAvailabilityFilter(craftsman, params.availability, availabilityAt)
              );
            }

            // ✅ 如果没有搜索词和类别筛选，合并所有已審核工匠（非 pending，含暫停服務）
            if (!params.searchTerm && !params.categoryId && !inferredCategoryId) {
              try {
                const { data: allActiveCraftsmen, error: allError } = await supabase
                  .from('craftsmen')
                  .select(`
                    *,
                    user:users!user_id(name, email, phone, profile_image)
                  `)
                  .neq('status', 'pending');
                
                if (!allError && allActiveCraftsmen) {
                  // 创建已存在工匠的 ID 集合（用于去重）
                  const seenIds = new Set(filteredData.map((c: any) => c.id));
                  
                  // 应用相同的过滤条件（除了距离）
                  let additionalCraftsmen = allActiveCraftsmen.filter((craftsman: any) => {
                    // 跳过已存在的工匠
                    if (seenIds.has(craftsman.id)) return false;
                    
                    // 应用其他过滤条件
                    if (params.minRating && (craftsman.rating || 0) < params.minRating) return false;
                    if (params.maxPrice && (craftsman.hourly_rate || 0) > params.maxPrice) return false;
                    if (params.availability && params.availability !== 'all') {
                      if (!this.matchesSearchAvailabilityFilter(craftsman, params.availability, availabilityAt)) return false;
                    }
                    if (CraftsmanService.effectiveBadgeLevelFromRow(craftsman) === 'risk') return false;
                    
                    return true;
                  });
                  
                  // 为额外工匠添加 name 和 profile_image 字段（从 user 关联获取）
                  additionalCraftsmen = additionalCraftsmen.map((craftsman: any) => ({
                    ...craftsman,
                    name: craftsman.user?.name || '未知工匠',
                    profile_image: craftsman.profile_image || craftsman.user?.profile_image
                  }));
                  
                  // 合并结果
                  filteredData = [...filteredData, ...additionalCraftsmen];
                  
                  console.log(`✅ 合并搜索结果：地理位置搜索 ${filteredData.length - additionalCraftsmen.length} 个，额外添加 ${additionalCraftsmen.length} 个工匠`);
                }
              } catch (mergeError) {
                console.error('合并所有工匠时出错:', mergeError);
                // 即使合并失败，也继续使用地理位置搜索结果
              }
            }

            // ✅ 批量查询所有工匠的评价数量（实时计算）
            const craftsmanIdsForReviews = filteredData.map((c: any) => c.id).filter((id: any) => id);
            if (craftsmanIdsForReviews.length > 0) {
              const { data: allCraftsmanReviews, error: reviewsCountError } = await supabase
                .from('reviews')
                .select('craftsman_id')
                .in('craftsman_id', craftsmanIdsForReviews);
              
              if (!reviewsCountError && allCraftsmanReviews) {
                // 计算每个工匠的评价数量
                const reviewsCountMap: Record<string, number> = {};
                allCraftsmanReviews.forEach((review: any) => {
                  const craftsmanId = review.craftsman_id;
                  reviewsCountMap[craftsmanId] = (reviewsCountMap[craftsmanId] || 0) + 1;
                });
                
                // 更新 filteredData 中的 total_reviews
                filteredData = filteredData.map((craftsman: any) => ({
                  ...craftsman,
                  total_reviews: reviewsCountMap[craftsman.id] || 0
                }));
              }
            }

            filteredData = await this.mergeServiceAreasFromDb(filteredData);

            const mappedCraftsmen = filteredData
              .filter((craftsman: any) => CraftsmanService.effectiveBadgeLevelFromRow(craftsman) !== 'risk')
              .map((craftsman: any) => {
                const { availability, isOnBreak } = this.computeAvailability(
                  craftsman,
                  availabilityAt
                );
                
                // 调试日志：检查原始 profile_image 数据
                if (craftsman.name === 'phongfix890') {
                  console.log('🔍 phongfix890 profile_image 原始数据:', {
                    hasProfileImage: !!craftsman.profile_image,
                    profileImageType: typeof craftsman.profile_image,
                    profileImageLength: craftsman.profile_image?.length || 0,
                    profileImagePreview: craftsman.profile_image?.substring(0, 50) || 'null',
                    isDataUri: craftsman.profile_image?.startsWith('data:') || false
                  });
                }
                
                // 处理 profile_image：优先使用 craftsman.profile_image，否则从 user 关联获取
                const profileImageSource = craftsman.profile_image || craftsman.user?.profile_image;
                const profileImage = this.convertProfileImageUrl(profileImageSource);
                
                // 调试日志：检查转换后的 profileImage
                if (craftsman.name === 'phongfix890') {
                  console.log('🔍 phongfix890 profileImage 转换后:', {
                    profileImage,
                    profileImageLength: profileImage?.length || 0,
                    profileImagePreview: profileImage?.substring(0, 50) || 'null',
                    isDataUri: profileImage?.startsWith('data:') || false
                  });
                }
                
                return {
                  id: craftsman.id,
                  name: craftsman.name || craftsman.user?.name || '未知工匠',
                  profileImage,
                  rating: craftsman.rating || 0,
                  numReviews: craftsman.total_reviews || 0,
                  specialties: craftsman.specialties || [],
                  serviceAreas: craftsman.service_areas || craftsman.serviceAreas || [],
                  location: {
                    lat: craftsman.location?.lat || 0,
                    lng: craftsman.location?.lng || 0,
                  },
                  availability,
                  responseTime: '5 min',
                  hourlyRate: craftsman.hourly_rate || 0,
                  isPremium: craftsman.is_premium || false,
                  premiumUntil: craftsman.premium_until ? new Date(craftsman.premium_until) : undefined,
                  totalEarnings: craftsman.total_earnings || 0,
                  completedJobs: craftsman.completed_jobs || 0,
                  accountBalance: craftsman.account_balance || 0,
                  isOnBreak,
                  workingHours: this.parseWorkingHours(craftsman.working_hours),
                  badgeLevel: CraftsmanService.effectiveBadgeLevelFromRow(craftsman),
                  _availabilityContext: { status: craftsman.status, is_on_break: craftsman.is_on_break },
                };
              })
              .sort((a: any, b: any) => {
                const ra = availabilitySortRank(a.availability);
                const rb = availabilitySortRank(b.availability);
                if (ra !== rb) return ra - rb;
                const badgeScore = (level: string | undefined) =>
                  level === 'excellent' ? 30 : level === 'good' ? 10 : level === 'warning' ? -10 : 0;
                const scoreA = (a.rating || 0) + badgeScore(a.badgeLevel);
                const scoreB = (b.rating || 0) + badgeScore(b.badgeLevel);
                return scoreB - scoreA;
              });
            return await this.populateWorkingHours(mappedCraftsmen, availabilityAt);
          }
        } catch (error) {
          console.log('地理位置搜索失敗，使用普通搜索:', error);
        }
      }

      // 如果地理位置搜索失敗或沒有結果，使用普通查詢（列出非 pending，含暫停服務）
      let query = supabase
        .from('craftsmen')
        .select(`
          *,
          user:users!user_id(name, email, phone, profile_image)
        `)
        .neq('status', 'pending');

      // 根據搜索詞過濾
      if (params.searchTerm) {
        // ✅ 增强搜索：根据搜索词推断类别
        let inferredCategoryId = params.categoryId;
        if (!inferredCategoryId) {
          inferredCategoryId = this.mapSearchTermToCategory(params.searchTerm);
        }

        const allResults: any[] = [];
        const seenIds = new Set<string>();

        // 1. 如果推断出类别，先搜索该类别下的所有工匠
        if (inferredCategoryId) {
          const { data: categoryResults, error: categoryError } = await supabase
            .from('craftsmen')
            .select(`
              *,
              user:users!user_id(name, email, phone, profile_image)
            `)
            .neq('status', 'pending')
            .contains('specialties', [inferredCategoryId]);

          if (!categoryError && categoryResults) {
            categoryResults.forEach((craftsman: any) => {
              if (!seenIds.has(craftsman.id)) {
                allResults.push(craftsman);
                seenIds.add(craftsman.id);
              }
            });
          }
        }

        // 2. 搜索名称和描述中包含搜索词的工匠
        const { data: searchResults, error: searchError } = await supabase
          .from('craftsmen')
          .select(`
            *,
            user:users!user_id(name, email, phone, profile_image)
          `)
          .neq('status', 'pending')
          .or(`user.name.ilike.%${params.searchTerm}%,description.ilike.%${params.searchTerm}%`);

        if (!searchError && searchResults) {
          searchResults.forEach((craftsman: any) => {
            if (!seenIds.has(craftsman.id)) {
              allResults.push(craftsman);
              seenIds.add(craftsman.id);
            }
          });
        }

        if (searchError && !inferredCategoryId) {
          console.error('Error searching craftsmen:', searchError);
          return [];
        }

        let filteredData = allResults;

        if (params.minRating) {
          filteredData = filteredData.filter((craftsman: any) =>
            craftsman.rating >= params.minRating
          );
        }

        if (params.maxPrice) {
          filteredData = filteredData.filter((craftsman: any) =>
            craftsman.hourly_rate <= params.maxPrice
          );
        }

        if (params.availability && params.availability !== 'all') {
          filteredData = filteredData.filter((craftsman: any) =>
            this.matchesSearchAvailabilityFilter(craftsman, params.availability, availabilityAt)
          );
        }

        if (params.serviceCity?.trim()) {
          filteredData = CraftsmanService.filterByServiceCity(
            filteredData,
            params.serviceCity.trim()
          );
        }

        // ✅ 批量查询所有工匠的评价数量（实时计算）
        const craftsmanIdsForReviews = filteredData.map((c: any) => c.id).filter((id: any) => id);
        if (craftsmanIdsForReviews.length > 0) {
          const { data: allCraftsmanReviews, error: reviewsCountError } = await supabase
            .from('reviews')
            .select('craftsman_id')
            .in('craftsman_id', craftsmanIdsForReviews);
          
          if (!reviewsCountError && allCraftsmanReviews) {
            // 计算每个工匠的评价数量
            const reviewsCountMap: Record<string, number> = {};
            allCraftsmanReviews.forEach((review: any) => {
              const craftsmanId = review.craftsman_id;
              reviewsCountMap[craftsmanId] = (reviewsCountMap[craftsmanId] || 0) + 1;
            });
            
            // 更新 filteredData 中的 total_reviews
            filteredData = filteredData.map((craftsman: any) => ({
              ...craftsman,
              total_reviews: reviewsCountMap[craftsman.id] || 0
            }));
          }
        }

        const mappedCraftsmen = filteredData
          .filter((craftsman: any) => CraftsmanService.effectiveBadgeLevelFromRow(craftsman) !== 'risk')
          .map((craftsman: any) => {
            const { availability, isOnBreak } = this.computeAvailability(
              craftsman,
              availabilityAt
            );
            return {
              id: craftsman.id,
              name: craftsman.user?.name || '未知工匠',
              profileImage: this.convertProfileImageUrl(craftsman.user?.profile_image),
              rating: craftsman.rating || 0,
              numReviews: craftsman.total_reviews || 0,
              specialties: craftsman.specialties || [],
              serviceAreas: craftsman.service_areas || craftsman.serviceAreas || [],
              location: craftsman.location || { lat: 0, lng: 0 },
              availability,
              responseTime: '5 min',
              hourlyRate: craftsman.hourly_rate || 0,
              isPremium: craftsman.is_premium || false,
              premiumUntil: craftsman.premium_until ? new Date(craftsman.premium_until) : undefined,
              totalEarnings: craftsman.total_earnings || 0,
              completedJobs: craftsman.completed_jobs || 0,
              accountBalance: craftsman.account_balance || 0,
              isOnBreak,
              workingHours: this.parseWorkingHours(craftsman.working_hours),
              badgeLevel: CraftsmanService.effectiveBadgeLevelFromRow(craftsman),
              _availabilityContext: { status: craftsman.status, is_on_break: craftsman.is_on_break },
            };
          })
          .sort((a: any, b: any) => {
            const ra = availabilitySortRank(a.availability);
            const rb = availabilitySortRank(b.availability);
            if (ra !== rb) return ra - rb;
            const scoreA = ExposureService.calculateExposureScore(a);
            const scoreB = ExposureService.calculateExposureScore(b);
            return scoreB - scoreA;
          });
        return await this.populateWorkingHours(mappedCraftsmen, availabilityAt);
      }

      // 如果没有搜索词，使用原来的查询逻辑
      // ✅ 修復：應用 categoryId 過濾
      if (params.categoryId) {
        query = query.contains('specialties', [params.categoryId]);
      }

      // ✅ 修復：應用其他過濾條件
      if (params.minRating) {
        query = query.gte('rating', params.minRating);
      }

      if (params.maxPrice) {
        query = query.lte('hourly_rate', params.maxPrice);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error searching craftsmen:', error);
        // ✅ 如果嚴格搜索失敗，嘗試分階段搜索
        if (params.categoryId || params.location) {
          console.log('嘗試分階段搜索...');
          return await this.searchWithRelaxedConditions(params);
        }
        return [];
      }

      // ✅ 批量查询所有工匠的评价数量（实时计算）
      let dataWithReviews = (data || []).filter((craftsman: any) =>
        !params.availability || params.availability === 'all'
          ? true
          : this.matchesSearchAvailabilityFilter(craftsman, params.availability, availabilityAt)
      );
      if (params.serviceCity?.trim()) {
        dataWithReviews = CraftsmanService.filterByServiceCity(
          dataWithReviews,
          params.serviceCity.trim()
        );
      }
      if (dataWithReviews.length > 0) {
        const craftsmanIdsForReviews = dataWithReviews.map((c: any) => c.id).filter((id: any) => id);
        if (craftsmanIdsForReviews.length > 0) {
          const { data: allCraftsmanReviews, error: reviewsCountError } = await supabase
            .from('reviews')
            .select('craftsman_id')
            .in('craftsman_id', craftsmanIdsForReviews);
          
          if (!reviewsCountError && allCraftsmanReviews) {
            // 计算每个工匠的评价数量
            const reviewsCountMap: Record<string, number> = {};
            allCraftsmanReviews.forEach((review: any) => {
              const craftsmanId = review.craftsman_id;
              reviewsCountMap[craftsmanId] = (reviewsCountMap[craftsmanId] || 0) + 1;
            });
            
            // 更新 dataWithReviews 中的 total_reviews
            dataWithReviews = dataWithReviews.map((craftsman: any) => ({
              ...craftsman,
              total_reviews: reviewsCountMap[craftsman.id] || 0
            }));
          }
        }
      }

      let results =
        dataWithReviews
          .filter((craftsman: any) => CraftsmanService.effectiveBadgeLevelFromRow(craftsman) !== 'risk')
          .map((craftsman: any) => {
          const { availability, isOnBreak } = this.computeAvailability(
            craftsman,
            availabilityAt
          );
          const rating = craftsman.rating || 0;
          const totalReviews = craftsman.total_reviews || 0;
          return {
            id: craftsman.id,
            name: craftsman.user?.name || '未知工匠',
            profileImage: this.convertProfileImageUrl(craftsman.user?.profile_image),
            rating,
            numReviews: totalReviews,
            specialties: craftsman.specialties || [],
            serviceAreas: craftsman.service_areas || craftsman.serviceAreas || [],
            location: craftsman.location || { lat: 0, lng: 0 },
            availability,
            responseTime: '5 min',
            hourlyRate: craftsman.hourly_rate || 0,
            isPremium: craftsman.is_premium || false,
            premiumUntil: craftsman.premium_until ? new Date(craftsman.premium_until) : undefined,
            totalEarnings: craftsman.total_earnings || 0,
            completedJobs: craftsman.completed_jobs || 0,
            accountBalance: craftsman.account_balance || 0,
            isOnBreak,
            workingHours: this.parseWorkingHours(craftsman.working_hours),
            badgeLevel: CraftsmanService.effectiveBadgeLevelFromRow(craftsman),
            _availabilityContext: { status: craftsman.status, is_on_break: craftsman.is_on_break },
          };
        }) || [];

      results.sort((a, b) => {
        const d = availabilitySortRank(a.availability) - availabilitySortRank(b.availability);
        if (d !== 0) return d;
        return ExposureService.calculateExposureScore(b) - ExposureService.calculateExposureScore(a);
      });

      // ✅ 如果嚴格搜索沒有結果，嘗試分階段搜索
      if (results.length === 0 && (params.categoryId || params.location)) {
        console.log('嚴格搜索無結果，嘗試放寬條件...');
        return await this.searchWithRelaxedConditions(params);
      }

      return await this.populateWorkingHours(results, availabilityAt);
    } catch (error) {
      console.error('Error searching craftsmen:', error);
      return [];
    }
  }

  // ✅ 分階段搜索：逐步放寬條件
  private static async searchWithRelaxedConditions(params: {
    categoryId?: number;
    location?: { lat: number; lng: number };
    radius?: number;
    availability?: string;
    minRating?: number;
    maxPrice?: number;
    availabilityReferenceTime?: Date;
  }): Promise<Craftsman[]> {
    console.log('🔍 開始分階段搜索...');

    // 階段 1：放寬位置限制（如果有限制）
    if (params.location && params.radius) {
      console.log('階段 1: 放寬位置限制（擴大搜索半徑）...');
      try {
        const { data, error } = await supabase
          .rpc('get_nearby_craftsmen', {
            user_lat: params.location.lat,
            user_lng: params.location.lng,
            search_radius: (params.radius || 10) * 2  // 擴大2倍
          });

        if (!error && data && data.length > 0) {
          let filtered = data;
          
          // 仍然應用 categoryId（如果提供）
          if (params.categoryId) {
            filtered = filtered.filter((c: any) =>
              c.specialties && c.specialties.includes(params.categoryId)
            );
          }

          if (filtered.length > 0) {
            console.log(`✅ 階段 1 成功，找到 ${filtered.length} 位工匠`);
            filtered = await this.mergeServiceAreasFromDb(filtered);
            const updatedData = await this.updateCraftsmenReviewsCount(filtered);
            const mapped = await this.populateWorkingHours(
              this.mapCraftsmenData(updatedData, params.availabilityReferenceTime),
              params.availabilityReferenceTime
            );
            return mapped.filter((c) => c.badgeLevel !== 'risk');
          }
        }
      } catch (error) {
        console.log('階段 1 失敗，繼續下一階段...');
      }
    }

    // 階段 2：忽略位置，僅根據 categoryId 和 availability
    if (params.categoryId) {
      console.log('階段 2: 忽略位置限制，僅根據專業技能搜索...');
      try {
        const { data, error } = await supabase
          .from('craftsmen')
          .select(`*, user:users!user_id(name, email, phone, profile_image)`)
          .neq('status', 'pending')
          .contains('specialties', [params.categoryId]);

        if (!error && data && data.length > 0) {
          const filtered =
            params.availability && params.availability !== 'all'
              ? data.filter((c: any) => this.matchesSearchAvailabilityFilter(c, params.availability, params.availabilityReferenceTime))
              : data;
          if (filtered.length > 0) {
            console.log(`✅ 階段 2 成功，找到 ${filtered.length} 位工匠`);
            const updatedData = await this.updateCraftsmenReviewsCount(filtered);
            const mapped = await this.populateWorkingHours(
              this.mapCraftsmenData(updatedData, params.availabilityReferenceTime),
              params.availabilityReferenceTime
            );
            return mapped.filter((c) => c.badgeLevel !== 'risk');
          }
        }
      } catch (error) {
        console.log('階段 2 失敗，繼續下一階段...');
      }
    }

    // 階段 3：僅根據 availability（忽略 categoryId 和 location）
    console.log('階段 3: 搜索已審核工匠（忽略專業技能和位置）...');
    try {
      const { data, error } = await supabase
        .from('craftsmen')
        .select(`*, user:users!user_id(name, email, phone, profile_image)`)
        .neq('status', 'pending');

      if (!error && data && data.length > 0) {
        let filtered =
          params.availability && params.availability !== 'all'
            ? data.filter((c: any) => this.matchesSearchAvailabilityFilter(c, params.availability, params.availabilityReferenceTime))
            : data;
        if (params.categoryId) {
          filtered = filtered.filter(
            (c: any) => Array.isArray(c.specialties) && c.specialties.includes(params.categoryId)
          );
        }
        if (filtered.length > 0) {
          console.log(`✅ 階段 3 成功，找到 ${filtered.length} 位工匠`);
          const updatedData = await this.updateCraftsmenReviewsCount(filtered);
          const mapped = await this.populateWorkingHours(this.mapCraftsmenData(updatedData));
          return mapped.filter((c) => c.badgeLevel !== 'risk');
        }
      }
    } catch (error) {
      console.log('階段 3 失敗...');
    }

    // 階段 4：所有已審核工匠（最後選項）
    console.log('階段 4: 返回所有已審核工匠...');
    try {
      const { data, error } = await supabase
        .from('craftsmen')
        .select(`*, user:users!user_id(name, email, phone, profile_image)`)
        .neq('status', 'pending');

      if (!error && data && data.length > 0) {
        let filtered =
          params.availability && params.availability !== 'all'
            ? data.filter((c: any) => this.matchesSearchAvailabilityFilter(c, params.availability, params.availabilityReferenceTime))
            : data;
        if (params.categoryId) {
          filtered = filtered.filter(
            (c: any) => Array.isArray(c.specialties) && c.specialties.includes(params.categoryId)
          );
        }
        if (filtered.length > 0) {
          console.log(`✅ 階段 4 成功，找到 ${filtered.length} 位工匠`);
          const updatedData = await this.updateCraftsmenReviewsCount(filtered);
          const mapped = await this.populateWorkingHours(
            this.mapCraftsmenData(updatedData, params.availabilityReferenceTime),
            params.availabilityReferenceTime
          );
          return mapped.filter((c) => c.badgeLevel !== 'risk');
        }
      }
    } catch (error) {
      console.error('階段 4 失敗:', error);
    }

    console.log('❌ 所有階段都失敗，返回空結果');
    return [];
  }

  /** 搜尋／列表用：有效徽章（含管理員覆寫與 RPC 已算好之 badge_level） */
  private static effectiveBadgeLevelFromRow(row: any): NonNullable<Craftsman['badgeLevel']> {
    return resolveCraftsmanBadgeLevelForRow(row);
  }

  // ✅ 輔助方法：更新工匠数据中的评价数量（实时计算）
  private static async updateCraftsmenReviewsCount(data: any[]): Promise<any[]> {
    if (data.length === 0) return data;
    
    const craftsmanIds = data.map((c: any) => c.id).filter((id: any) => id);
    if (craftsmanIds.length === 0) return data;
    
    const { data: allCraftsmanReviews, error: reviewsCountError } = await supabase
      .from('reviews')
      .select('craftsman_id')
      .in('craftsman_id', craftsmanIds);
    
    if (reviewsCountError || !allCraftsmanReviews) {
      return data; // 如果查询失败，返回原始数据
    }
    
    // 计算每个工匠的评价数量
    const reviewsCountMap: Record<string, number> = {};
    allCraftsmanReviews.forEach((review: any) => {
      const craftsmanId = review.craftsman_id;
      reviewsCountMap[craftsmanId] = (reviewsCountMap[craftsmanId] || 0) + 1;
    });
    
    // 更新数据中的 total_reviews
    return data.map((craftsman: any) => ({
      ...craftsman,
      total_reviews: reviewsCountMap[craftsman.id] || 0
    }));
  }

  // ✅ 輔助方法：映射工匠數據
  private static mapCraftsmenData(data: any[], availabilityAt?: Date): Craftsman[] {
    return data.map((craftsman: any) => {
      const { availability, isOnBreak } = this.computeAvailability(
        craftsman,
        availabilityAt
      );
      const rating = craftsman.rating || 0;
      const totalReviews = craftsman.total_reviews || 0;
      return {
        id: craftsman.id,
        name: craftsman.user?.name || craftsman.name || '未知工匠',
        profileImage: this.convertProfileImageUrl(craftsman.user?.profile_image || craftsman.profile_image),
        rating,
        numReviews: totalReviews,
        specialties: craftsman.specialties || [],
        location: craftsman.location || { lat: 0, lng: 0 },
        availability,
        responseTime: '5 min',
        hourlyRate: craftsman.hourly_rate || 0,
        isPremium: craftsman.is_premium || false,
        premiumUntil: craftsman.premium_until ? new Date(craftsman.premium_until) : undefined,
        totalEarnings: craftsman.total_earnings || 0,
        completedJobs: craftsman.completed_jobs || 0,
        accountBalance: craftsman.account_balance || 0,
        isOnBreak,
        workingHours: this.parseWorkingHours(craftsman.working_hours),
        badgeLevel: CraftsmanService.effectiveBadgeLevelFromRow(craftsman),
        _availabilityContext: { status: craftsman.status, is_on_break: craftsman.is_on_break },
      };
    });
  }

  // 獲取熱門工匠
  static async getPopularCraftsmen(limit: number = 10): Promise<Craftsman[]> {
    try {
      const { data, error } = await supabase
        .from('craftsmen')
        .select(`
          *,
          user:users!user_id(name, email, phone, profile_image)
        `)
        .eq('status', 'active')
        .order('rating', { ascending: false })
        .order('completed_jobs', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching popular craftsmen:', error);
        return [];
      }

      const mapped =
        data?.map((craftsman: any) => {
          const { availability, isOnBreak } = this.computeAvailability(craftsman);
          return {
            id: craftsman.id,
            name: craftsman.user?.name || '未知工匠',
            profileImage: this.convertProfileImageUrl(craftsman.user?.profile_image),
            rating: craftsman.rating || 0,
            numReviews: craftsman.total_reviews || 0,
            specialties: craftsman.specialties || [],
            location: craftsman.location || { lat: 0, lng: 0 },
            availability,
            responseTime: '5 min',
            hourlyRate: craftsman.hourly_rate || 0,
            isPremium: craftsman.is_premium || false,
            premiumUntil: craftsman.premium_until ? new Date(craftsman.premium_until) : undefined,
            totalEarnings: craftsman.total_earnings || 0,
            completedJobs: craftsman.completed_jobs || 0,
            accountBalance: craftsman.account_balance || 0,
            isOnBreak,
            workingHours: this.parseWorkingHours(craftsman.working_hours),
            _availabilityContext: { status: craftsman.status, is_on_break: craftsman.is_on_break },
          };
        }) || [];
      return await this.populateWorkingHours(mapped as Craftsman[]);
    } catch (error) {
      console.error('Error fetching popular craftsmen:', error);
      return [];
    }
  }

  // ✅ 低优先级：获取优秀工匠（首页推荐）
  static async getExcellentCraftsmen(limit: number = 10): Promise<Craftsman[]> {
    try {
      // 获取评分 ≥4.8 且评价数 ≥10 的工匠
      const { data, error } = await supabase
        .from('craftsmen')
        .select(`
          *,
          user:users!user_id(name, email, phone, profile_image)
        `)
        .eq('status', 'active')
        .gte('rating', 4.8)
        .gte('total_reviews', 10)
        .order('rating', { ascending: false })
        .order('completed_jobs', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching excellent craftsmen:', error);
        return [];
      }

      const updatedData = await this.updateCraftsmenReviewsCount(data || []);
      return await this.populateWorkingHours(this.mapCraftsmenData(updatedData));
    } catch (error) {
      console.error('Error fetching excellent craftsmen:', error);
      return [];
    }
  }

  // ✅ 低优先级：获取类别推荐工匠（每个类别前3名）
  static async getCategoryTopCraftsmen(categoryId: number, limit: number = 3): Promise<Craftsman[]> {
    try {
      const { data, error } = await supabase
        .from('craftsmen')
        .select(`
          *,
          user:users!user_id(name, email, phone, profile_image)
        `)
        .eq('status', 'active')
        .contains('specialties', [categoryId])
        .gte('rating', 4.0) // 至少4.0分
        .order('rating', { ascending: false })
        .order('completed_jobs', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching category top craftsmen:', error);
        return [];
      }

      const updatedData = await this.updateCraftsmenReviewsCount(data || []);
      return await this.populateWorkingHours(this.mapCraftsmenData(updatedData));
    } catch (error) {
      console.error('Error fetching category top craftsmen:', error);
      return [];
    }
  }

  // 獲取推薦工匠（基於用戶位置和偏好）
  static async getRecommendedCraftsmen(
    userIdOrLimit: string | number,
    location?: { lat: number; lng: number },
    limit: number = 10
  ): Promise<Craftsman[]> {
    // If first parameter is a number, treat it as limit and use popular craftsmen
    if (typeof userIdOrLimit === 'number') {
      return this.getPopularCraftsmen(userIdOrLimit);
    }
    
    // Otherwise, use the existing logic
    const userId = userIdOrLimit;
    if (!location) {
      return [];
    }
    
    try {
      // 首先獲取用戶的偏好（如果有）
      // 注意：user_preferences 表可能不存在，使用 try-catch 處理
      let userPreferences = null;
      try {
        const { data, error: prefQueryError } = await supabase
          .from('user_preferences')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();
        if (!prefQueryError) {
          userPreferences = data;
        }
      } catch (prefError: any) {
        // 表不存在或沒有記錄，忽略錯誤
        if (prefError?.code !== 'PGRST116' && prefError?.code !== 'PGRST205') {
          console.warn('無法獲取用戶偏好設定:', prefError);
        }
      }

      // 獲取附近的工匠
      const { data, error } = await supabase
        .rpc('get_nearby_craftsmen', {
          user_lat: location.lat,
          user_lng: location.lng,
          search_radius: 20
        });

      if (error) {
        console.error('Error fetching recommended craftsmen:', error);
        return [];
      }

      const enriched = await this.mergeServiceAreasFromDb(data || []);

      // 根據用戶偏好排序
      let sortedData = enriched;

      if (userPreferences) {
        sortedData = sortedData.sort((a: any, b: any) => {
          let scoreA = 0;
          let scoreB = 0;

          // 根據評分計算分數
          scoreA += (a.rating || 0) * 2;
          scoreB += (b.rating || 0) * 2;

          // 根據完成案件數計算分數
          scoreA += (a.completed_jobs || 0) * 0.1;
          scoreB += (b.completed_jobs || 0) * 0.1;

          // 注意：user_preferences 表中沒有 preferred_categories 欄位
          // 如果需要根據用戶偏好排序，可以從其他來源獲取（例如服務歷史）

          return scoreB - scoreA;
        });
      }

      const mapped = sortedData.slice(0, limit).map((craftsman: any) => {
        const { availability, isOnBreak } = this.computeAvailability(craftsman);
        return {
          id: craftsman.id,
          name: craftsman.name,
          profileImage: this.convertProfileImageUrl(craftsman.profile_image),
          rating: craftsman.rating || 0,
          numReviews: craftsman.total_reviews || 0,
          specialties: craftsman.specialties || [],
          location: {
            lat: craftsman.location?.lat || 0,
            lng: craftsman.location?.lng || 0,
          },
          availability,
          responseTime: '5 min',
          hourlyRate: craftsman.hourly_rate || 0,
          isPremium: craftsman.is_premium || false,
          premiumUntil: craftsman.premium_until ? new Date(craftsman.premium_until) : undefined,
          totalEarnings: craftsman.total_earnings || 0,
          completedJobs: craftsman.completed_jobs || 0,
          accountBalance: craftsman.account_balance || 0,
          isOnBreak,
          workingHours: this.parseWorkingHours(craftsman.working_hours),
          serviceAreas: craftsman.service_areas || craftsman.serviceAreas || [],
          _availabilityContext: { status: craftsman.status, is_on_break: craftsman.is_on_break },
        };
      });
      return await this.populateWorkingHours(mapped as Craftsman[]);
    } catch (error) {
      console.error('Error fetching recommended craftsmen:', error);
      return [];
    }
  }

  // 獲取公司儀表板統計數據
  static async getCompanyDashboardStats(companyId: string): Promise<{
    totalEarnings: number;
    completedJobs: number;
    pendingJobs: number;
    averageRating: number;
    totalReviews: number;
    thisMonthEarnings: number;
    totalCustomers: number;
    responseRate: number;
    totalEmployees: number;
    activeEmployees: number;
    employeeEfficiency: number;
    totalPackageValue: number;
    thisMonthPackageValue: number;
    averagePackageValue: number;
    signedContracts: number;
    activeGuarantees: number;
    pendingDeposits: number;
    inProgressJobs: number;
    confirmedContracts: number;
    // ✅ 新增字段：分別返回原始尾款和追加報價的待收款
    pendingOriginalTailPayment: number;
    pendingAdditionalQuotationPayment: number;
    totalAdditionalQuotationAmount: number;
  }> {
    try {
      // 獲取公司基本資料
      const { data: companyData } = await supabase
        .from('companies')
        .select('employee_count')  // 只選擇存在的字段
        .eq('id', companyId)
        .single();

      // ✅ 修改：查询真实的员工数据
      const { data: employeesData, error: employeesError } = await supabase
        .from('employees')
        .select('id, status')
        .eq('company_id', companyId);

      // ✅ 修改：使用真实数据计算员工统计
      let totalEmployees = employeesData?.length || 0;
      // ✅ "在線員工"（可接案）= 状态为 'active' 或 'busy' 的员工
      let activeEmployees = employeesData?.filter(
        emp => emp.status === 'active' || emp.status === 'busy'
      ).length || 0;

      // 如果查询失败，使用 companyData.employee_count 作为后备
      if (employeesError) {
        console.warn('獲取員工數據失敗，使用備用數據:', employeesError);
        const fallbackTotal = companyData?.employee_count || 0;
        totalEmployees = fallbackTotal;
        activeEmployees = Math.floor(fallbackTotal * 0.8); // 后备方案
      }

      const hourlyRate = 0;  // 默認值

      // ✅ 僅計入「公司已確認入帳」：status=completed，且財務已核准（與收入頁確認入帳流程一致）
      // ✅ 方法 1：直接通過 company_id 查詢付款記錄
      const { data: earningsData, error: earningsError } = await supabase
        .from('payments')
        .select('amount, paid_amount, deposit_amount, remaining_amount, payment_type, craftsman_earnings, created_at, status, company_id, request_id')
        .eq('company_id', companyId)
        .eq('status', 'completed')
        .eq('finance_review_status', 'approved');

      // ✅ 方法 2：如果直接查詢沒有結果，通過 service_requests 表間接查詢
      let indirectPaymentsData: any[] = [];
      if ((!earningsData || earningsData.length === 0) && !earningsError) {
        console.log(`[getCompanyDashboardStats] 直接查詢無結果，嘗試通過 service_requests 間接查詢...`);
        
        // 獲取該公司的所有服務請求 ID
        const { data: serviceRequests } = await supabase
          .from('service_requests')
          .select('id')
          .eq('company_id', companyId);
        
        if (serviceRequests && serviceRequests.length > 0) {
          const requestIds = serviceRequests.map(r => r.id);
          console.log(`[getCompanyDashboardStats] 找到 ${requestIds.length} 個服務請求，查詢對應的付款記錄...`);
          
          const { data: indirectPayments, error: indirectError } = await supabase
            .from('payments')
            .select('amount, paid_amount, deposit_amount, remaining_amount, payment_type, craftsman_earnings, created_at, status, company_id, request_id')
            .in('request_id', requestIds)
            .eq('status', 'completed')
            .eq('finance_review_status', 'approved');
          
          if (indirectPayments) {
            indirectPaymentsData = indirectPayments;
            console.log(`[getCompanyDashboardStats] 間接查詢找到 ${indirectPayments.length} 筆付款記錄`);
            
            // ✅ 如果間接查詢找到付款記錄但 company_id 為 null，更新它們
            const paymentsToUpdate = indirectPayments.filter((p: any) => !p.company_id);
            if (paymentsToUpdate.length > 0) {
              console.log(`[getCompanyDashboardStats] 發現 ${paymentsToUpdate.length} 筆付款記錄缺少 company_id，嘗試更新...`);
              for (const payment of paymentsToUpdate) {
                await supabase
                  .from('payments')
                  .update({ company_id: companyId })
                  .eq('id', (payment as any).id);
              }
            }
          }
          
          if (indirectError) {
            console.error('[getCompanyDashboardStats] 間接查詢付款記錄失敗:', indirectError);
          }
        }
      }

      // 合併直接查詢和間接查詢的結果
      const allPaymentsData = [...(earningsData || []), ...indirectPaymentsData];
      
      // ✅ 添加調試日誌，幫助診斷數據不一致問題
      console.log(`[getCompanyDashboardStats] 公司ID: ${companyId}, 查詢付款記錄結果:`, {
        directQueryCount: earningsData?.length || 0,
        indirectQueryCount: indirectPaymentsData.length,
        totalPaymentsCount: allPaymentsData.length,
        paymentsData: allPaymentsData.slice(0, 5), // 只顯示前 5 筆
        error: earningsError,
        paymentsDetails: allPaymentsData.map((p: any) => ({
          id: p.id,
          request_id: p.request_id,
          company_id: p.company_id,
          status: p.status,
          paid_amount: p.paid_amount,
          deposit_amount: p.deposit_amount,
          amount: p.amount
        }))
      });

      if (earningsError) {
        console.error('[getCompanyDashboardStats] 獲取付款數據失敗:', earningsError);
      }

      // ✅ 實收口徑改為扣佣後：優先使用 craftsman_earnings（公司案沿用歷史欄位名）
      // 若為部分入帳，按 paid_amount / amount 比例分攤淨額，與佣金頁入帳認列邏輯一致。
      const getNetReceivedAmount = (payment: {
        amount?: number | null;
        paid_amount?: number | null;
        deposit_amount?: number | null;
        remaining_amount?: number | null;
        payment_type?: string | null;
        craftsman_earnings?: number | null;
      }): number => {
        const amount = Math.max(Number(payment.amount || 0), 0);
        const paid = Number(payment.paid_amount || 0);
        const paidBasis = paid > 0
          ? Math.min(paid, amount > 0 ? amount : paid)
          : payment.payment_type === 'deposit'
            ? Math.max(Number(payment.deposit_amount || 0), 0)
            : payment.payment_type === 'remaining'
              ? Math.max(Number(payment.remaining_amount || 0), 0)
              : amount;

        const net = Number(payment.craftsman_earnings || 0);
        if (net > 0) {
          if (amount > 0 && paidBasis > 0) {
            return (net * paidBasis) / amount;
          }
          return net;
        }

        // 後備：若舊資料缺少 craftsman_earnings，退回既有已付口徑
        return paidBasis;
      };

      const totalEarnings = allPaymentsData?.reduce(
        (sum, payment) => sum + getNetReceivedAmount(payment),
        0
      ) || 0;

      console.log(`[getCompanyDashboardStats] 計算的總收入: ${totalEarnings}`);

      // 獲取本月收入
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const thisMonthEarnings = allPaymentsData
        ?.filter(payment => new Date(payment.created_at) >= startOfMonth)
        .reduce((sum, payment) => sum + getNetReceivedAmount(payment), 0) || 0;

      // 獲取已完成案件數
      const { data: completedJobsData } = await supabase
        .from('service_requests')
        .select('id')
        .eq('company_id', companyId)
        .eq('status', 'completed');

      const completedJobs = completedJobsData?.length || 0;

      // 獲取待處理案件數
      const { data: pendingJobsData } = await supabase
        .from('service_requests')
        .select('id')
        .eq('company_id', companyId)
        .in('status', ['pending', 'confirmed']);

      const pendingJobs = pendingJobsData?.length || 0;

      // 統包計費相關統計
      const { data: serviceRequestsData } = await supabase
        .from('service_requests')
        .select(
          'id, status, pricing_mode, fixed_total_price, estimated_price, created_at, completed_at, company_id, craftsman_id'
        )
        .eq('company_id', companyId);

      const packageRequests = (serviceRequestsData || []).filter((request) =>
        CraftsmanService.rowIsCompanyFixed(request)
      );

      // ✅ 改進：獲取所有統包案件的追加報價總額
      const packageRequestIds = packageRequests.map(r => r.id);
      let totalAdditionalQuotationAmount = 0;
      let allAdditionalQuotations: any[] = [];

      if (packageRequestIds.length > 0) {
        const { data: additionalQuotations } = await supabase
          .from('additional_quotations')
          .select('service_request_id, amount, status')
          .in('service_request_id', packageRequestIds)
          .in('status', ['approved', 'pending']); // 只計算已批准或待審核的追加報價
        
        if (additionalQuotations) {
          allAdditionalQuotations = additionalQuotations;
          totalAdditionalQuotationAmount = additionalQuotations.reduce(
            (sum, q) => sum + Number(q.amount || 0),
            0
          );
        }
      }

      // ✅ 改進：totalPackageValue 包含追加報價
      const baseTotalPackageValue = packageRequests.reduce((sum: number, request: any) => {
        const price = request.fixed_total_price ?? request.estimated_price ?? 0;
        return sum + price;
      }, 0);
      const totalPackageValue = baseTotalPackageValue + totalAdditionalQuotationAmount;

      // ✅ 改進：thisMonthPackageValue 也包含追加報價
      const baseThisMonthPackageValue = packageRequests
        .filter(request => request.created_at && new Date(request.created_at) >= startOfMonth)
        .reduce((sum: number, request: any) => {
          const price = request.fixed_total_price ?? request.estimated_price ?? 0;
          return sum + price;
        }, 0);
      
      // 計算本月新增的追加報價
      let thisMonthAdditionalQuotationAmount = 0;
      if (packageRequestIds.length > 0) {
        const { data: thisMonthQuotations } = await supabase
          .from('additional_quotations')
          .select('amount, created_at')
          .in('service_request_id', packageRequestIds)
          .in('status', ['approved', 'pending'])
          .gte('created_at', startOfMonth.toISOString());
        
        if (thisMonthQuotations) {
          thisMonthAdditionalQuotationAmount = thisMonthQuotations.reduce(
            (sum, q) => sum + Number(q.amount || 0),
            0
          );
        }
      }
      const thisMonthPackageValue = baseThisMonthPackageValue + thisMonthAdditionalQuotationAmount;

      // ✅ 改進：平均統包不包含追加報價（因為追加報價是額外的）
      const averagePackageValue = packageRequests.length > 0
        ? baseTotalPackageValue / packageRequests.length
        : 0;

      const signedContracts = packageRequests.filter(request =>
        ['confirmed', 'in_progress', 'completed'].includes(request.status)
      ).length;

      const inProgressJobs = packageRequests.filter(request => request.status === 'in_progress').length;

      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

      const activeGuarantees = packageRequests.filter(request => {
        if (request.status !== 'completed' || !request.completed_at) {
          return false;
        }
        const completedAt = new Date(request.completed_at);
        return completedAt >= oneYearAgo;
      }).length;

      // 待收簽約訂金：尚未收到訂金的統包案件
      // 包含：
      // 1. status === 'pending' 的統包案件（還沒有確認收訂金）
      // 2. status === 'confirmed' 但付款記錄中訂金未付清的統包案件
      const pendingPackageRequests = packageRequests.filter(
        request => request.status === 'pending'
      );
      const confirmedPackageRequests = packageRequests.filter(
        request => request.status === 'confirmed'
      );
      
      // 獲取所有已確認統包案件的付款記錄
      const confirmedRequestIds = confirmedPackageRequests.map(r => r.id);
      let pendingDepositsFromConfirmed = 0;
      
      if (confirmedRequestIds.length > 0) {
        const { data: paymentRecords } = await supabase
          .from('payments')
          .select('request_id, status, deposit_amount, paid_amount')
          .in('request_id', confirmedRequestIds);
        
        // ✅ 修復：找出沒有付款記錄，或付款記錄中訂金未付清的案件
        // 訂金已收到的狀態應包含：completed, paid, approved
        pendingDepositsFromConfirmed = confirmedPackageRequests.filter(request => {
          const payment = paymentRecords?.find(p => p.request_id === request.id);
          // 沒有付款記錄，或付款狀態不是已付款狀態，或訂金未付清
          if (!payment) return true;
          if (!['completed', 'paid', 'approved'].includes(payment.status)) return true;
          // 檢查訂金是否已付清（假設訂金是總額的30%）
          const totalAmount = request.fixed_total_price ?? request.estimated_price ?? 0;
          const expectedDeposit = Math.max(
            Math.round(totalAmount * 0.3),
            PAYMENT_CONFIG.FIXED_DEPOSIT
          );
          const paidDeposit = Number(payment.paid_amount || 0);
          return paidDeposit < expectedDeposit;
        }).length;
      }
      
      // ✅ 修復：待收訂金 = pending 案件數 + confirmed 但訂金未付清的案件數
      const pendingDeposits = pendingPackageRequests.length + pendingDepositsFromConfirmed;

      // ✅ 改進：計算待收尾款（分別計算原始尾款和追加報價）
      // 獲取所有統包案件的付款記錄
      const { data: allPackagePayments } = await supabase
        .from('payments')
        .select('request_id, payment_type, paid_amount, deposit_amount, remaining_amount, amount, status')
        .in('request_id', packageRequestIds);

      // 計算原始統包金額的待收尾款
      let pendingOriginalTailPayment = 0;
      let pendingAdditionalQuotationPayment = 0;

      packageRequests.forEach((request: any) => {
        const baseAmount = request.fixed_total_price ?? request.estimated_price ?? 0;
        const expectedDeposit = Math.max(
          Math.round(baseAmount * 0.3),
          PAYMENT_CONFIG.FIXED_DEPOSIT
        );
        const expectedRemaining = baseAmount - expectedDeposit;
        
        // 查找該案件的付款記錄
        const payments = allPackagePayments?.filter(p => p.request_id === request.id) || [];
        const originalPayment = payments.find(p => p.payment_type !== 'additional');
        const paidAmount = originalPayment?.paid_amount || 0;
        
        // 計算原始尾款待收款
        if (paidAmount < baseAmount) {
          // 如果付款記錄中有 remaining_amount，使用它
          const remainingAmount = originalPayment?.remaining_amount != null 
            ? Number(originalPayment.remaining_amount)
            : Math.max(expectedRemaining - Math.max(paidAmount - expectedDeposit, 0), 0);
          pendingOriginalTailPayment += remainingAmount;
        }
        
        // 計算追加報價待收款
        const requestQuotations = allAdditionalQuotations.filter((q: any) => q.service_request_id === request.id) || [];
        const additionalPayments = payments.filter(p => p.payment_type === 'additional');
        const totalQuotationAmount = requestQuotations.reduce((sum: number, q: any) => sum + Number(q.amount || 0), 0);
        const paidQuotationAmount = additionalPayments.reduce((sum: number, p: any) => sum + Number(p.paid_amount || 0), 0);
        pendingAdditionalQuotationPayment += Math.max(totalQuotationAmount - paidQuotationAmount, 0);
      });

      const confirmedContracts = packageRequests.filter(request =>
        request.status === 'confirmed'
      ).length;

      // 獲取評分和評價
      const { data: reviewsData } = await supabase
        .from('reviews')
        .select('rating')
        .eq('company_id', companyId);

      const totalReviews = reviewsData?.length || 0;
      const averageRating = totalReviews > 0 
        ? reviewsData!.reduce((sum, review) => sum + review.rating, 0) / totalReviews 
        : 0;

      // 獲取客戶數量
      const { data: customersData } = await supabase
        .from('service_requests')
        .select('user_id')
        .eq('company_id', companyId);

      const uniqueCustomers = new Set(customersData?.map(req => req.user_id) || []).size;

      // 計算員工相關統計
      // ✅ activeEmployees 已在上面使用真实数据计算，不再使用假数据
      const employeeEfficiency = totalEmployees > 0 ? (completedJobs / totalEmployees) : 0;

      // 計算回應率
      const { data: responseData } = await supabase
        .from('service_requests')
        .select('id, response_time')
        .eq('company_id', companyId)
        .not('response_time', 'is', null);

      const responseRate = totalReviews > 0 ? (responseData?.length || 0) / totalReviews * 100 : 0;

      return {
        totalEarnings,
        completedJobs,
        pendingJobs,
        averageRating,
        totalReviews,
        thisMonthEarnings,
        totalCustomers: uniqueCustomers,
        responseRate,
        totalEmployees,
        activeEmployees,
        employeeEfficiency,
        totalPackageValue,
        thisMonthPackageValue,
        averagePackageValue,
        signedContracts,
        activeGuarantees,
        pendingDeposits,
        inProgressJobs,
        confirmedContracts,
        // ✅ 新增字段：分別返回原始尾款和追加報價的待收款
        pendingOriginalTailPayment,
        pendingAdditionalQuotationPayment,
        totalAdditionalQuotationAmount
      };
    } catch (error) {
      console.error('Error fetching company dashboard stats:', error);
      return {
        totalEarnings: 0,
        completedJobs: 0,
        pendingJobs: 0,
        averageRating: 0,
        totalReviews: 0,
        thisMonthEarnings: 0,
        totalCustomers: 0,
        responseRate: 0,
        totalEmployees: 0,
        activeEmployees: 0,
        employeeEfficiency: 0,
        totalPackageValue: 0,
        thisMonthPackageValue: 0,
        averagePackageValue: 0,
        signedContracts: 0,
        activeGuarantees: 0,
        pendingDeposits: 0,
        inProgressJobs: 0,
        confirmedContracts: 0,
        // ✅ 新增字段
        pendingOriginalTailPayment: 0,
        pendingAdditionalQuotationPayment: 0,
        totalAdditionalQuotationAmount: 0
      };
    }
  }

  // 獲取公司最近案件
  static async getCompanyRecentJobs(companyId: string, limit: number = 5): Promise<any[]> {
    try {
      const { data: jobs, error } = await supabase
        .from('service_requests')
        .select(`
          id,
          title,
          description,
          status,
          estimated_price,
          fixed_total_price,
          pricing_mode,
          created_at,
          scheduled_date,
          location,
          urgency,
          user_id
        `)
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching company recent jobs:', error);
        return [];
      }

      // ✅ 修復：改進錯誤處理，避免查詢失敗導致整個函數失敗
      const userIds = (jobs || [])
        .map(job => job.user_id)
        .filter((id): id is string => typeof id === 'string' && id.trim() !== '');

      let customerLookup: Record<string, { name?: string; email?: string; phone?: string; profile_image?: string }> = {};
      if (userIds.length > 0) {
        try {
          const uniqueIds = Array.from(new Set(userIds));
          
          // ✅ 修復：限制查詢數量，避免查詢過多用戶導致超時
          const limitedIds = uniqueIds.slice(0, 100); // 限制最多查詢 100 個用戶
          
          const { data: customers, error: customersError } = await supabase
            .from('users')
            .select('id, name, email, phone, profile_image')
            .in('id', limitedIds);

          if (customersError) {
            console.error('Error fetching customers for recent jobs:', customersError);
            // ✅ 即使查詢失敗，也繼續執行，只是不顯示客戶信息
            customerLookup = {};
          } else {
            customerLookup = (customers || []).reduce<typeof customerLookup>((acc, customer) => {
              if (customer.id) {
                acc[customer.id] = {
                  name: customer.name || '未知客戶',
                  email: customer.email || '',
                  phone: customer.phone || '',
                  profile_image: customer.profile_image || null
                };
              }
              return acc;
            }, {});
          }
        } catch (customerError) {
          console.error('Exception while fetching customers for recent jobs:', customerError);
          // ✅ 即使發生異常，也繼續執行，只是不顯示客戶信息
          customerLookup = {};
        }
      }

      return jobs?.map(job => {
        const customer = job.user_id ? customerLookup[job.user_id] : undefined;
        const pricingMode = job.pricing_mode ?? 'hourly';
        const fixedTotalPrice = job.fixed_total_price ?? null;
        const estimatedPrice = job.estimated_price ?? 0;
        const baseAmount = pricingMode === 'company_fixed'
          ? (fixedTotalPrice ?? estimatedPrice ?? 0)
          : (estimatedPrice ?? 0);
        const depositAmount = pricingMode === 'company_fixed'
          ? Math.max(Math.round(baseAmount * 0.3), PAYMENT_CONFIG.FIXED_DEPOSIT)
          : PAYMENT_CONFIG.FIXED_DEPOSIT;
        const remainingAmount = Math.max(baseAmount - depositAmount, 0);
        
        return {
          id: job.id,
          title: job.title,
          description: job.description,
          status: job.status,
          amount: baseAmount,
          pricingMode,
          fixedTotalPrice,
          depositAmount,
          remainingAmount,
          customer_name: customer?.name,
          customer_email: customer?.email,
          customer_phone: customer?.phone,
          customer_profile_image: customer?.profile_image,
          created_at: new Date(job.created_at)
        };
      }) || [];
    } catch (error) {
      console.error('Error fetching company recent jobs:', error);
      return [];
    }
  }

  // 獲取公司收入趨勢（與財務儀表板現金流同口徑：財務已核准 + paid/completed/approved，依 paid_at 優先歸月）
  static async getCompanyEarningsTrend(companyId: string, months: number = 6): Promise<{
    labels: string[];
    earnings: number[];
    jobs: number[];
  }> {
    try {
      const labels: string[] = [];
      const earnings: number[] = [];
      const jobs: number[] = [];
      const now = new Date();

      let approvedRows: any[] = [];
      try {
        approvedRows = await EmployeeJobService.getCompanyApprovedFinancePaymentRows(companyId);
      } catch (e) {
        console.error('獲取公司核准付款列失敗:', e);
        approvedRows = [];
      }

      for (let i = months - 1; i >= 0; i--) {
        try {
          const targetDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const monthKey = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}`;

          const monthlyEarnings = (approvedRows || []).reduce((sum, payment) => {
            const d = companyFinancePaymentRecognizedAt(payment);
            if (!d) return sum;
            const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
            if (mk !== monthKey) return sum;
            return sum + companyFinancePaymentNetAmount(payment);
          }, 0);

          labels.push(targetDate.toLocaleDateString('zh-TW', { month: 'short' }));
          earnings.push(monthlyEarnings);
          jobs.push(0);
        } catch (monthError) {
          console.error(`處理月份數據時發生錯誤 (i=${i}):`, monthError);
        }
      }

      return { labels, earnings, jobs };
    } catch (error) {
      console.error('Error fetching company earnings trend:', error);
      return { labels: [], earnings: [], jobs: [] };
    }
  }

  // 獲取公司績效指標
  static async getCompanyPerformanceMetrics(companyId: string): Promise<{
    completionRate: number;
    completionRateThisMonth: number;
    averageResponseTime: number;
    responseSampleCount: number;
    customerSatisfaction: number;
    ratingSampleCount: number;
    repeatCustomers: number;
    repeatCustomerBase: number;
    employeeEfficiency: number;
    totalEmployees: number;
    totalJobs: number;
    completedJobs: number;
    thisMonthTotalJobs: number;
    thisMonthCompletedJobs: number;
  }> {
    try {
      // 獲取所有案件
      const { data: allJobs } = await supabase
        .from('service_requests')
        .select('id, status, response_time, created_at')
        .eq('company_id', companyId);

      // 計算完成率
      const totalJobs = allJobs?.length || 0;
      const completedJobs = allJobs?.filter(job => job.status === 'completed').length || 0;
      const completionRate = totalJobs > 0 ? (completedJobs / totalJobs) * 100 : 0;
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);
      const thisMonthJobs = (allJobs || []).filter((job: any) => {
        const raw = job?.created_at || null;
        if (!raw) return false;
        const dt = new Date(raw);
        return !Number.isNaN(dt.getTime()) && dt >= startOfMonth;
      });
      const thisMonthTotalJobs = thisMonthJobs.length;
      const thisMonthCompletedJobs = thisMonthJobs.filter((job: any) => job.status === 'completed').length;
      const completionRateThisMonth =
        thisMonthTotalJobs > 0 ? (thisMonthCompletedJobs / thisMonthTotalJobs) * 100 : 0;

      // 計算平均回應時間
      const responseTimes = allJobs
        ?.filter(job => job.response_time)
        .map(job => job.response_time) || [];
      const averageResponseTime = responseTimes.length > 0 
        ? responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length 
        : 0;
      const responseSampleCount = responseTimes.length;

      // 計算客戶滿意度
      const { data: reviewsData } = await supabase
        .from('reviews')
        .select('rating')
        .eq('company_id', companyId);

      const customerSatisfaction = reviewsData && reviewsData.length > 0
        ? reviewsData.reduce((sum, review) => sum + review.rating, 0) / reviewsData.length
        : 0;
      const ratingSampleCount = reviewsData?.length || 0;

      // 計算重複客戶
      const { data: customersData } = await supabase
        .from('service_requests')
        .select('user_id')
        .eq('company_id', companyId);

      const customerCounts = {};
      customersData?.forEach(req => {
        customerCounts[req.user_id] = (customerCounts[req.user_id] || 0) + 1;
      });

      const repeatCustomers = Object.values(customerCounts).filter(count => count > 1).length;
      const repeatCustomerBase = Object.keys(customerCounts).length;

      // 計算員工效率
      const { data: companyData } = await supabase
        .from('companies')
        .select('employee_count')
        .eq('id', companyId)
        .single();

      const totalEmployees = companyData?.employee_count || 0;
      const employeeEfficiency = totalEmployees > 0 ? (completedJobs / totalEmployees) : 0;

      return {
        completionRate,
        completionRateThisMonth,
        averageResponseTime,
        responseSampleCount,
        customerSatisfaction,
        ratingSampleCount,
        repeatCustomers,
        repeatCustomerBase,
        employeeEfficiency,
        totalEmployees,
        totalJobs,
        completedJobs,
        thisMonthTotalJobs,
        thisMonthCompletedJobs
      };
    } catch (error) {
      console.error('Error fetching company performance metrics:', error);
      return {
        completionRate: 0,
        completionRateThisMonth: 0,
        averageResponseTime: 0,
        responseSampleCount: 0,
        customerSatisfaction: 0,
        ratingSampleCount: 0,
        repeatCustomers: 0,
        repeatCustomerBase: 0,
        employeeEfficiency: 0,
        totalEmployees: 0,
        totalJobs: 0,
        completedJobs: 0,
        thisMonthTotalJobs: 0,
        thisMonthCompletedJobs: 0
      };
    }
  }

  // 獲取公司所有案件
  static async getCompanyJobs(companyId: string, filters?: {
    status?: string;
    dateRange?: { start: Date; end: Date };
    searchTerm?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    pricingMode?: 'company_fixed' | 'hourly' | 'all';
    limit?: number;
  }): Promise<{
    jobs: any[];
    total: number;
    stats: {
      pending: number;
      confirmed: number;
      inProgress: number;
      completed: number;
      cancelled: number;
    };
  }> {
    try {
      // 设置默认日期范围（如果未提供）
      const defaultDateRange = {
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30天前
        end: new Date()
      };
      const dateRange = filters?.dateRange || defaultDateRange;

      // 優化查詢：先獲取 service_requests 不帶 joins，然後分別獲取相關數據以提高性能
      let query = supabase
        .from('service_requests')
        .select(`
          id,
          title,
          description,
          status,
          pricing_mode,
          fixed_total_price,
          estimated_price,
          completed_at,
          created_at,
          scheduled_date,
          location,
          urgency,
          assigned_employee_id,
          assigned_department,
          schedule_notes,
          site_contact_name,
          site_contact_phone,
          started_at,
          notes,
          user_id
        `)
        .eq('company_id', companyId)
        .gte('created_at', dateRange.start.toISOString())
        .lte('created_at', dateRange.end.toISOString())
        .limit(filters?.limit ?? 100);

      // 應用過濾器
      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.searchTerm) {
        query = query.or(`title.ilike.%${filters.searchTerm}%,description.ilike.%${filters.searchTerm}%`);
      }

      // 應用排序（移除重複的 order）
      if (filters?.sortBy) {
        const order = filters.sortOrder || 'desc';
        query = query.order(filters.sortBy, { ascending: order === 'asc' });
      } else {
        query = query.order('created_at', { ascending: false });
      }

      const { data: jobs, error } = await query;

      if (error) {
        console.error('Error fetching company jobs:', error);
        // 如果是超時錯誤，提供更詳細的錯誤信息
        if (error.code === '57014' || error.message?.includes('timeout')) {
          console.error('Query timeout - consider reducing date range or adding database indexes');
        }
        return { jobs: [], total: 0, stats: { pending: 0, confirmed: 0, inProgress: 0, completed: 0, cancelled: 0 } };
      }

      if (!jobs || jobs.length === 0) {
        return { jobs: [], total: 0, stats: { pending: 0, confirmed: 0, inProgress: 0, completed: 0, cancelled: 0 } };
      }

      // 獲取所有相關的 user_id 和 employee_id
      const userIds = [...new Set(jobs.map(job => job.user_id).filter(Boolean))];
      const employeeIds = [...new Set(jobs.map(job => job.assigned_employee_id).filter(Boolean))];

      // 並行獲取客戶和員工數據
      const [customersResult, employeesResult] = await Promise.all([
        userIds.length > 0
          ? supabase
              .from('users')
              .select('id, name, email, phone, profile_image')
              .in('id', userIds)
          : Promise.resolve({ data: [], error: null }),
        employeeIds.length > 0
          ? supabase
              .from('employees')
              .select('id, name, email, phone, employee_number, department')
              .in('id', employeeIds)
          : Promise.resolve({ data: [], error: null })
      ]);

      // 創建查找映射
      const customersMap = new Map(
        (customersResult.data || []).map(customer => [customer.id, customer])
      );
      const employeesMap = new Map(
        (employeesResult.data || []).map(employee => [employee.id, employee])
      );

      // 將客戶和員工數據附加到 jobs
      const jobsWithRelations = jobs.map(job => ({
        ...job,
        customer: job.user_id ? customersMap.get(job.user_id) || null : null,
        assigned_employee: job.assigned_employee_id ? employeesMap.get(job.assigned_employee_id) || null : null
      }));

      const jobIds = jobsWithRelations.map(job => job.id);

      // ✅ 改進 1：查詢付款記錄（包含 payment_type 以區分訂金和尾款）
      let paymentsByJob: Record<string, any> = {};
      let tailPaymentsByJob: Record<string, any> = {}; // ✅ 新增：專門存儲尾款付款記錄
      if (jobIds.length > 0) {
        const { data: payments, error: paymentsError } = await supabase
          .from('payments')
          .select('id, request_id, status, paid_amount, deposit_amount, remaining_amount, paid_at, payment_method, finance_review_status, payment_type, created_at, updated_at')
          .in('request_id', jobIds)
          .eq('company_id', companyId);

        if (paymentsError) {
          console.error('Error fetching payments:', paymentsError);
        } else {
          // ✅ 改進：分別處理訂金付款和尾款付款
          (payments || []).forEach((payment) => {
            if (payment.request_id) {
              // 優先使用非追加報價的付款記錄作為主要付款記錄
              if (payment.payment_type !== 'additional') {
                if (!paymentsByJob[payment.request_id] || payment.payment_type === 'remaining') {
                  // 如果已有付款記錄，但當前是尾款付款，則優先使用尾款付款
                  paymentsByJob[payment.request_id] = payment;
                }
              }
              
              // ✅ 專門存儲尾款付款記錄
              if (payment.payment_type === 'remaining') {
                tailPaymentsByJob[payment.request_id] = payment;
              }
            }
          });
        }
      }

      let departmentLinksByJob: Record<string, string[]> = {};
      if (jobIds.length > 0) {
        const { data: departmentLinks, error: departmentLinksError } = await supabase
          .from('service_request_department_links')
          .select('service_request_id, department')
          .in('service_request_id', jobIds);

        if (departmentLinksError) {
          const ignorableCodes = new Set(['42P01', 'PGRST205']); // table missing in SQL or REST cache
          if (!ignorableCodes.has(departmentLinksError.code || '')) {
            console.error('Error fetching job department links:', departmentLinksError);
          }
        } else {
          departmentLinksByJob = (departmentLinks || []).reduce<Record<string, string[]>>((acc, row) => {
            const key = row.service_request_id;
            if (!key) {
              return acc;
            }
            if (!acc[key]) {
              acc[key] = [];
            }
            if (row.department && !acc[key].includes(row.department)) {
              acc[key].push(row.department);
            }
            return acc;
          }, {});
        }
      }

      let assignmentsByJob: Record<string, any[]> = {};
      if (jobIds.length > 0) {
        const { data: assignmentsData, error: assignmentsError } = await supabase
          .from('service_request_assignments')
          .select(
            `
              id,
              service_request_id,
              role,
              assigned_at,
              employee_id,
              employee:employees!service_request_assignments_employee_id_fkey(
                id,
                name,
                email,
                phone,
                employee_number,
                department,
                status
              )
            `
          )
          .in('service_request_id', jobIds);

        if (assignmentsError) {
          console.error('Error fetching job assignments:', assignmentsError);
        } else {
          assignmentsByJob = (assignmentsData || []).reduce<Record<string, any[]>>((acc, assignment) => {
            const serviceRequestId = assignment.service_request_id;
            if (!acc[serviceRequestId]) {
              acc[serviceRequestId] = [];
            }
            acc[serviceRequestId].push({
              id: assignment.id,
              role: assignment.role,
              assignedAt: assignment.assigned_at ? new Date(assignment.assigned_at) : null,
              employeeId: assignment.employee_id,
              employee: assignment.employee
                ? {
                    id: assignment.employee.id,
                    name: assignment.employee.name,
                    email: assignment.employee.email,
                    phone: assignment.employee.phone,
                    employee_number: assignment.employee.employee_number,
                    department: assignment.employee.department,
                    status: assignment.employee.status
                  }
                : null
            });
            return acc;
          }, {});
        }
      }

      // 獲取統計數據 - 使用並行 count 查詢並應用日期範圍過濾以提高性能
      // 並行執行各狀態的 count 查詢
      const [pendingResult, confirmedResult, inProgressResult, completedResult, cancelledResult] = await Promise.all([
        supabase
          .from('service_requests')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('status', 'pending')
          .gte('created_at', dateRange.start.toISOString())
          .lte('created_at', dateRange.end.toISOString()),
        supabase
          .from('service_requests')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('status', 'confirmed')
          .gte('created_at', dateRange.start.toISOString())
          .lte('created_at', dateRange.end.toISOString()),
        supabase
          .from('service_requests')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('status', 'in_progress')
          .gte('created_at', dateRange.start.toISOString())
          .lte('created_at', dateRange.end.toISOString()),
        supabase
          .from('service_requests')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('status', 'completed')
          .gte('created_at', dateRange.start.toISOString())
          .lte('created_at', dateRange.end.toISOString()),
        supabase
          .from('service_requests')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('status', 'cancelled')
          .gte('created_at', dateRange.start.toISOString())
          .lte('created_at', dateRange.end.toISOString())
      ]);

      // 如果統計查詢失敗，使用已獲取的 jobs 數據計算統計
      let stats = {
        pending: 0,
        confirmed: 0,
        inProgress: 0,
        completed: 0,
        cancelled: 0,
      };

      // 使用 count 結果，如果查詢失敗則從 jobs 數據計算
      stats = {
        pending: pendingResult.count ?? jobsWithRelations?.filter(job => job.status === 'pending').length ?? 0,
        confirmed: confirmedResult.count ?? jobsWithRelations?.filter(job => job.status === 'confirmed').length ?? 0,
        inProgress: inProgressResult.count ?? jobsWithRelations?.filter(job => job.status === 'in_progress').length ?? 0,
        completed: completedResult.count ?? jobsWithRelations?.filter(job => job.status === 'completed').length ?? 0,
        cancelled: cancelledResult.count ?? jobsWithRelations?.filter(job => job.status === 'cancelled').length ?? 0,
      };

      const formattedJobs = jobsWithRelations?.map((job: any) => {
        const pricingMode = job.pricing_mode ?? 'hourly';
        const fixedTotalPrice = job.fixed_total_price ?? null;
        const estimatedPrice = job.estimated_price ?? 0;
        const baseAmount =
          pricingMode === 'company_fixed'
            ? (fixedTotalPrice ?? estimatedPrice)
            : estimatedPrice;

        const departmentList =
          (departmentLinksByJob[job.id]?.length ?? 0) > 0
            ? departmentLinksByJob[job.id]
            : job.assigned_department
            ? [job.assigned_department]
            : [];

        // ✅ 改進 1：附加付款記錄信息
        const payment = paymentsByJob[job.id] || null;
        // ✅ 新增：獲取尾款付款記錄（如果存在）
        const tailPayment = tailPaymentsByJob[job.id] || null;
        // ✅ 改進：優先使用尾款付款記錄（如果存在），否則使用主要付款記錄
        const displayPayment = tailPayment || payment;
        const depositAmount = payment?.deposit_amount || 0;
        const paidAmount = payment?.paid_amount || 0;
        // ✅ 改進：只有當付款狀態已確認或財務已審核時，才視為已收訂金
        const depositReceived = payment && (
          payment.status === 'paid' || 
          payment.status === 'completed' || 
          payment.status === 'approved' ||
          payment.finance_review_status === 'approved' ||
          // ✅ 改進：只有当金额足够且状态不是pending时才认为"已收到"
          (paidAmount > 0 && paidAmount >= depositAmount && 
           payment.status !== 'pending' && 
           payment.finance_review_status !== 'pending')
        );

        return {
          id: job.id,
          title: job.title,
          description: job.description,
          status: job.status,
          amount: baseAmount ?? 0,
          pricingMode,
          fixedTotalPrice,
          estimatedPrice,
          completedAt: job.completed_at ? new Date(job.completed_at) : null,
          assigned_employee_id: job.assigned_employee_id,
          assigned_department: job.assigned_department ?? null,
          assigned_departments: departmentList,
          assigned_employee: job.assigned_employee
            ? {
                id: job.assigned_employee.id,
                name: job.assigned_employee.name,
                email: job.assigned_employee.email,
                phone: job.assigned_employee.phone,
                employee_number: job.assigned_employee.employee_number
              }
            : null,
          assignments: assignmentsByJob[job.id] || [],
          // ✅ 改進 1：添加付款記錄信息
          payment: displayPayment, // ✅ 優先使用尾款付款記錄（如果存在）
          depositAmount: depositAmount,
          paidAmount: paidAmount,
          depositReceived: depositReceived,
          paymentStatus: displayPayment?.status || null,
          paidAt: displayPayment?.paid_at ? new Date(displayPayment.paid_at) : null,
          customer: job.customer
            ? {
                id: job.customer.id,
                name: job.customer.name,
                email: job.customer.email,
                phone: job.customer.phone,
                profileImage: this.convertProfileImageUrl(job.customer.profile_image)
              }
            : null,
          created_at: new Date(job.created_at),
          scheduled_date: job.scheduled_date ? new Date(job.scheduled_date) : null,
          location: job.location,
          urgency: job.urgency || 'normal'
        };
      }) || [];

      return {
        jobs: formattedJobs,
        total: formattedJobs.length,
        stats
      };
    } catch (error) {
      console.error('Error fetching company jobs:', error);
      return { jobs: [], total: 0, stats: { pending: 0, confirmed: 0, inProgress: 0, completed: 0, cancelled: 0 } };
    }
  }

  static async updateServiceRequestDepartments(
    serviceRequestId: string,
    departments: string[]
  ): Promise<{ success: boolean; error?: string | null; departments?: string[] }> {
    try {
      const normalized = Array.from(
        new Set(
          (departments || []).filter((dept) => typeof dept === 'string' && dept.trim().length > 0)
        )
      );

      const lastDepartment = normalized.length > 0 ? normalized[normalized.length - 1] : null;

      const updateBase = {
        assigned_department: lastDepartment,
        updated_at: new Date().toISOString()
      };

      const updateWithArray = {
        ...updateBase,
        assigned_departments: normalized.length > 0 ? normalized : null
      };

      let { error: updateError } = await supabase
        .from('service_requests')
        .update(updateWithArray)
        .eq('id', serviceRequestId);

      if (updateError && updateError.code === '42703') {
        // Column missing on this environment, retry without it
        const { error: fallbackError } = await supabase
          .from('service_requests')
          .update(updateBase)
          .eq('id', serviceRequestId);
        updateError = fallbackError;
      }

      if (updateError) {
        console.error('更新案件部門失敗:', updateError);
        return { success: false, error: updateError.message };
      }

      return { success: true, departments: normalized };
    } catch (err) {
      console.error('更新案件部門時發生錯誤:', err);
      return { success: false, error: (err as Error).message };
    }
  }

  // 更新案件狀態
  static async updateJobStatus(jobId: string, status: string, notes?: string): Promise<boolean> {
    try {
      const updateData: any = { status };
      if (notes) {
        updateData.notes = notes;
      }

      const { error } = await supabase
        .from('service_requests')
        .update(updateData)
        .eq('id', jobId);

      if (error) {
        console.error('Error updating job status:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating job status:', error);
      return false;
    }
  }

  // 分配案件給員工
  static async assignJobToEmployee(jobId: string, employeeId: string): Promise<boolean> {
    try {
      // 先確認案件狀態是否允許指派
      const { data: requestData, error: requestFetchError } = await supabase
        .from('service_requests')
        .select('status')
        .eq('id', jobId)
        .single();

      if (requestFetchError || !requestData) {
        console.error('Failed to fetch service request before assignment:', requestFetchError);
        return false;
      }

      const allowedStatuses = new Set(['confirmed', 'in_progress']);
      if (!allowedStatuses.has(requestData.status)) {
        console.warn(
          `Service request ${jobId} is in status "${requestData.status}", assignment blocked until after deposit confirmation.`
        );
        return false;
      }

      const { error: assignmentError } = await supabase
        .from('service_request_assignments')
        .upsert(
          {
            service_request_id: jobId,
            employee_id: employeeId
          },
          {
            onConflict: 'service_request_id,employee_id'
          }
        );

      if (assignmentError) {
        console.error('Error assigning job to employee (assignment table):', assignmentError);
        return false;
      }

      const { error } = await supabase
        .from('service_requests')
        .update({ 
          assigned_employee_id: employeeId
        })
        .eq('id', jobId);

      if (error) {
        console.error('Error assigning job to employee:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error assigning job to employee:', error);
      return false;
    }
  }

  // 獲取公司員工列表（簡化版本）
  static async getCompanyEmployees(companyId: string): Promise<{
    employees: any[];
    total: number;
    stats: {
      total: number;
      active: number;
      busy: number;
      offline: number;
      averageRating: number;
      totalJobs: number;
    };
  }> {
    try {
      console.log('獲取公司員工，公司ID:', companyId);

      // 獲取公司員工資料 - 直接從 employees 表查詢
      const { data: employeesData, error: employeesError } = await supabase
        .from('employees')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (employeesError) {
        console.error('Error fetching employees data:', employeesError);
        return {
          employees: [],
          total: 0,
          stats: { total: 0, active: 0, busy: 0, offline: 0, averageRating: 0, totalJobs: 0 }
        };
      }

      console.log('獲取到的員工資料:', employeesData);

      const employees = employeesData?.map(employee => ({
        id: employee.id,
        employeeId: employee.id,
        userId: employee.user_id,
        name: employee.name,
        email: employee.email,
        phone: employee.phone,
        profileImage: '', // 員工暫時沒有頭像
        employeeNumber: employee.employee_number,
        department: employee.department,
        position: employee.position,
        hireDate: employee.hire_date,
        salary: employee.salary,
        specialties: employee.specialties || [],
        certifications: employee.certifications || [],
        experience: employee.experience,
        status: employee.status || 'active',
        createdAt: new Date(employee.created_at),
        rating: 0, // 員工暫時沒有評分系統
        totalReviews: 0,
        completedJobs: 0
      })) || [];

      // 計算統計數據
      const stats = {
        total: employees.length,
        active: employees.filter(emp => emp.status === 'active').length,
        busy: employees.filter(emp => emp.status === 'busy').length,
        offline: employees.filter(emp => emp.status === 'offline').length,
        averageRating: 0, // 員工暫時沒有評分
        totalJobs: 0 // 員工暫時沒有案件統計
      };

      return { employees, total: employees.length, stats };
    } catch (error) {
      console.error('Error fetching company employees:', error);
      return {
        employees: [],
        total: 0,
        stats: { total: 0, active: 0, busy: 0, offline: 0, averageRating: 0, totalJobs: 0 }
      };
    }
  }

  // 獲取員工詳細資料
  static async getEmployeeDetails(employeeId: string): Promise<any> {
    try {
      const { data, error } = await supabase
        .from('users')
        .select(`
          *,
          service_requests(
            id,
            title,
            status,
            created_at,
            estimated_price,
            customer:users!customer_id(name)
          ),
          reviews(
            id,
            rating,
            comment,
            created_at
          )
        `)
        .eq('id', employeeId)
        .single();

      if (error) {
        console.error('Error fetching employee details:', error);
        return null;
      }

      return {
        ...data,
        serviceRequests: data.service_requests || [],
        reviews: data.reviews || []
      };
    } catch (error) {
      console.error('Error fetching employee details:', error);
      return null;
    }
  }

  // 更新員工狀態
  static async updateEmployeeStatus(employeeId: string, status: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('users')
        .update({ status })
        .eq('id', employeeId);

      if (error) {
        console.error('Error updating employee status:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error updating employee status:', error);
      return false;
    }
  }

  // 創建員工（簡化版本，不創建 auth 用戶）
  static async createEmployee(employeeData: any): Promise<{ success: boolean; error: string | null }> {
    try {
      console.log('開始創建員工:', employeeData);

      // 檢查必要欄位
      if (!employeeData.name || !employeeData.email || !employeeData.phone) {
        return { success: false, error: '缺少必要欄位' };
      }

      // 檢查公司ID
      if (!employeeData.companyId) {
        return { success: false, error: '缺少公司ID' };
      }

      // 生成員工編號
      const employeeNumber = `EMP${Date.now()}`;

      // 直接創建員工記錄（不創建 auth 用戶）
      const employeeRecord = {
        company_id: employeeData.companyId,
        employee_number: employeeNumber,
        name: employeeData.name,
        email: employeeData.email,
        phone: employeeData.phone,
        address: employeeData.address || '',
        department: employeeData.department || '維修部',
        position: employeeData.position || '技師',
        hire_date: new Date().toISOString().split('T')[0],
        salary: employeeData.hourlyRate || 0,
        specialties: employeeData.specialties || [],
        certifications: employeeData.certifications || [],
        experience: employeeData.experience || '',
        status: 'active',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { error: employeeError } = await supabase
        .from('employees')
        .insert(employeeRecord);

      if (employeeError) {
        console.error('Error creating employee record:', employeeError);
        return { success: false, error: `創建員工記錄失敗: ${employeeError.message}` };
      }

      console.log('員工記錄創建成功');

      return { success: true, error: null };
    } catch (error) {
      console.error('Error creating employee:', error);
      return { success: false, error: `創建員工時發生錯誤: ${error.message}` };
    }
  }

  // 更新員工資料
  static async updateEmployee(employeeId: string, updateData: any): Promise<{ success: boolean; error: string | null }> {
    try {
      const { error } = await supabase
        .from('users')
        .update({
          name: updateData.name,
          phone: updateData.phone,
          status: updateData.status,
          updated_at: new Date().toISOString()
        })
        .eq('id', employeeId);

      if (error) {
        console.error('Error updating employee:', error);
        return { success: false, error: '更新員工資料失敗' };
      }

      // 更新員工詳細資料
      const { error: craftsmanError } = await supabase
        .from('craftsmen')
        .update({
          specialties: updateData.specialties,
          hourly_rate: updateData.hourlyRate,
          description: updateData.description,
          experience: updateData.experience,
          certifications: updateData.certifications,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', employeeId);

      if (craftsmanError) {
        console.error('Error updating employee details:', craftsmanError);
        return { success: false, error: '更新員工詳細資料失敗' };
      }

      return { success: true, error: null };
    } catch (error) {
      console.error('Error updating employee:', error);
      return { success: false, error: '更新員工時發生錯誤' };
    }
  }

  // 刪除員工
  static async deleteEmployee(employeeId: string): Promise<{ success: boolean; error: string | null }> {
    try {
      // 首先刪除員工詳細資料
      const { error: craftsmanError } = await supabase
        .from('craftsmen')
        .delete()
        .eq('user_id', employeeId);

      if (craftsmanError) {
        console.error('Error deleting employee details:', craftsmanError);
        return { success: false, error: '刪除員工詳細資料失敗' };
      }

      // 刪除用戶資料
      const { error: userError } = await supabase
        .from('users')
        .delete()
        .eq('id', employeeId);

      if (userError) {
        console.error('Error deleting user record:', userError);
        return { success: false, error: '刪除用戶記錄失敗' };
      }

      // 注意：這裡不刪除 auth 用戶，因為可能需要保留登入記錄
      // 如果需要完全刪除，可以調用 supabase.auth.admin.deleteUser()

      return { success: true, error: null };
    } catch (error) {
      console.error('Error deleting employee:', error);
      return { success: false, error: '刪除員工時發生錯誤' };
    }
  }

  // 獲取員工績效統計
  static async getEmployeePerformance(employeeId: string, period: string = 'month'): Promise<{
    completedJobs: number;
    totalEarnings: number;
    averageRating: number;
    responseTime: number;
    customerSatisfaction: number;
    jobsByMonth: { month: string; jobs: number }[];
  }> {
    try {
      // 獲取員工案件
      const { data: jobsData } = await supabase
        .from('service_requests')
        .select('id, status, created_at, estimated_price')
        .eq('assigned_employee_id', employeeId);

      const completedJobs = jobsData?.filter(job => job.status === 'completed').length || 0;
      const totalEarnings = jobsData
        ?.filter(job => job.status === 'completed')
        .reduce((sum, job) => sum + (job.estimated_price || 0), 0) || 0;

      // 獲取員工評價
      const { data: reviewsData } = await supabase
        .from('reviews')
        .select('rating')
        .eq('employee_id', employeeId);

      const averageRating = reviewsData && reviewsData.length > 0
        ? reviewsData.reduce((sum, review) => sum + review.rating, 0) / reviewsData.length
        : 0;

      // 計算回應時間（假設平均5分鐘）
      const responseTime = 5;

      // 客戶滿意度（基於評價）
      const customerSatisfaction = averageRating;

      // 按月份統計案件
      const jobsByMonth = [];
      const now = new Date();
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
        const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);

        const monthJobs = jobsData
          ?.filter(job => {
            const jobDate = new Date(job.created_at);
            return jobDate >= monthStart && jobDate <= monthEnd;
          }).length || 0;

        jobsByMonth.push({
          month: date.toLocaleDateString('zh-TW', { month: 'short', year: 'numeric' }),
          jobs: monthJobs
        });
      }

      return {
        completedJobs,
        totalEarnings,
        averageRating,
        responseTime,
        customerSatisfaction,
        jobsByMonth
      };
    } catch (error) {
      console.error('Error fetching employee performance:', error);
      return {
        completedJobs: 0,
        totalEarnings: 0,
        averageRating: 0,
        responseTime: 0,
        customerSatisfaction: 0,
        jobsByMonth: []
      };
    }
  }

  // 獲取公司收入統計
  static async getCompanyEarnings(companyId: string, period: string = 'month'): Promise<{
    totalEarnings: number;
    periodEarnings: number;
    totalJobs: number;
    completedJobs: number;
    averageJobValue: number;
    earningsByMonth: { month: string; earnings: number }[];
    earningsByCategory: { category: string; earnings: number }[];
    paymentMethods: { method: string; amount: number; count: number }[];
  }> {
    try {
      // 與財務儀表板同口徑：財務已核准 + paid/completed/approved；company_id 或工單所屬公司（去重）
      const allPaymentsData = await EmployeeJobService.getCompanyApprovedFinancePaymentRows(companyId);

      console.log(`[getCompanyEarnings] 公司ID: ${companyId}, 核准付款列筆數:`, allPaymentsData?.length || 0);

      const { data: jobsData } = await supabase
        .from('service_requests')
        .select('id, status')
        .eq('company_id', companyId);

      const totalJobs = jobsData?.length || 0;
      const completedJobs = jobsData?.filter(job => job.status === 'completed').length || 0;

      if (!allPaymentsData || allPaymentsData.length === 0) {
        console.log(`[getCompanyEarnings] 沒有找到核准入帳付款（公司ID: ${companyId}）`);
        return {
          totalEarnings: 0,
          periodEarnings: 0,
          totalJobs,
          completedJobs,
          averageJobValue: 0,
          earningsByMonth: [],
          earningsByCategory: [],
          paymentMethods: []
        };
      }

      // ✅ 與公司儀表板「實際已收收入 / 本月收入」一致：使用扣佣後淨額（優先 craftsman_earnings，部分付款按比例分攤）
      const paymentAmountForStats = (payment: (typeof allPaymentsData)[number]) =>
        companyFinancePaymentNetAmount(payment);

      const totalEarnings = allPaymentsData.reduce(
        (sum, payment) => sum + paymentAmountForStats(payment),
        0
      );

      const now = new Date();
      let periodStart: Date;

      switch (period) {
        case 'week':
          periodStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'quarter':
          periodStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
          break;
        case 'year':
          periodStart = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
      }

      const periodEarnings = allPaymentsData.reduce((sum, payment) => {
        const d = companyFinancePaymentRecognizedAt(payment);
        if (!d || d < periodStart) return sum;
        return sum + paymentAmountForStats(payment);
      }, 0);

      const averageJobValue = completedJobs > 0 ? totalEarnings / completedJobs : 0;

      // 按月份統計收入（依 paid_at 優先歸月，與財務趨勢一致）
      const earningsByMonth = [];
      for (let i = 11; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

        const monthEarnings = allPaymentsData.reduce((sum, payment) => {
          const d = companyFinancePaymentRecognizedAt(payment);
          if (!d) return sum;
          const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          if (mk !== monthKey) return sum;
          return sum + paymentAmountForStats(payment);
        }, 0);

        earningsByMonth.push({
          month: date.toLocaleDateString('zh-TW', { month: 'short', year: 'numeric' }),
          earnings: monthEarnings
        });
      }

      // ✅ 分別查詢服務請求（僅需 category_id 做類別統計）
      const requestIds = [...new Set(allPaymentsData.map(p => p.request_id).filter(Boolean))];
      let serviceRequestsMap = new Map();

      if (requestIds.length > 0) {
        const { data: serviceRequests } = await supabase
          .from('service_requests')
          .select('id, category_id')
          .in('id', requestIds);

        if (serviceRequests) {
          serviceRequestsMap = new Map(serviceRequests.map(sr => [sr.id, sr]));
        }
      }

      const categoryEarnings: { [key: string]: number } = {};
      allPaymentsData.forEach(payment => {
        const serviceRequest = serviceRequestsMap.get(payment.request_id);
        const category = serviceRequest?.category_id || '其他';
        categoryEarnings[category] = (categoryEarnings[category] || 0) + paymentAmountForStats(payment);
      });

      const earningsByCategory = Object.entries(categoryEarnings).map(([category, earnings]) => ({
        category,
        earnings
      }));

      const paymentMethodStats: { [key: string]: { amount: number; count: number } } = {};
      allPaymentsData.forEach(payment => {
        const method = payment.payment_method || '其他';
        if (!paymentMethodStats[method]) {
          paymentMethodStats[method] = { amount: 0, count: 0 };
        }
        paymentMethodStats[method].amount += paymentAmountForStats(payment);
        paymentMethodStats[method].count += 1;
      });

      const paymentMethods = Object.entries(paymentMethodStats).map(([method, stats]) => ({
        method,
        amount: stats.amount,
        count: stats.count
      }));

      return {
        totalEarnings,
        periodEarnings,
        totalJobs,
        completedJobs,
        averageJobValue,
        earningsByMonth,
        earningsByCategory,
        paymentMethods
      };
    } catch (error) {
      console.error('Error fetching company earnings:', error);
      return {
        totalEarnings: 0,
        periodEarnings: 0,
        totalJobs: 0,
        completedJobs: 0,
        averageJobValue: 0,
        earningsByMonth: [],
        earningsByCategory: [],
        paymentMethods: []
      };
    }
  }

  // 獲取公司財務摘要
  static async getCompanyFinancialSummary(companyId: string): Promise<{
    totalRevenue: number;
    totalExpenses: number;
    netProfit: number;
    profitMargin: number;
    averageRevenuePerJob: number;
    monthlyGrowth: number;
    /** 已核准主營支出（expenses）明細 */
    expenseLineItems: Array<{
      id: string;
      amount: number;
      category: string;
      date: string;
      description: string | null;
      vendor: string | null;
    }>;
    /** 已核准其他支出（other_transactions type=expense）明細 */
    otherExpenseLineItems: Array<{
      id: string;
      amount: number;
      category: string;
      date: string;
      description: string | null;
    }>;
  }> {
    try {
      // 與財務儀表板／公司收入頁同口徑：財務已核准 + paid/completed/approved（含工單所屬公司去重）
      const approvedRows = await EmployeeJobService.getCompanyApprovedFinancePaymentRows(companyId);

      console.log(`[getCompanyFinancialSummary] 公司ID: ${companyId}, 核准付款列筆數:`, approvedRows?.length || 0);

      const totalRevenue = (approvedRows || []).reduce(
        // ✅ 與公司儀表板一致：使用扣佣後淨額（優先 craftsman_earnings）
        (sum, payment) => sum + companyFinancePaymentNetAmount(payment),
        0
      );

      // ✅ 真實支出：全量加總（amount）+ 最近 500 筆明細（避免列表與總額不一致）
      const [
        { data: expensesSumRows, error: expensesSumError },
        { data: otherTxSumRows, error: otherTxSumError },
        { data: expensesData, error: expensesError },
        { data: otherTxData, error: otherTxError }
      ] = await Promise.all([
        supabase
          .from('expenses')
          .select('amount')
          .eq('company_id', companyId)
          .eq('status', 'approved'),
        supabase
          .from('other_transactions')
          .select('amount')
          .eq('company_id', companyId)
          .eq('type', 'expense')
          .eq('status', 'approved'),
        supabase
          .from('expenses')
          .select('id, amount, category, expense_date, description, vendor')
          .eq('company_id', companyId)
          .eq('status', 'approved')
          .order('expense_date', { ascending: false })
          .limit(500),
        supabase
          .from('other_transactions')
          .select('id, amount, category, transaction_date, description')
          .eq('company_id', companyId)
          .eq('type', 'expense')
          .eq('status', 'approved')
          .order('transaction_date', { ascending: false })
          .limit(500)
      ]);

      if (expensesSumError) {
        console.error('[getCompanyFinancialSummary] 獲取 expenses 加總失敗:', expensesSumError);
      }
      if (otherTxSumError) {
        console.error('[getCompanyFinancialSummary] 獲取 other_transactions 加總失敗:', otherTxSumError);
      }
      if (expensesError) {
        console.error('[getCompanyFinancialSummary] 獲取 expenses 明細失敗:', expensesError);
      }
      if (otherTxError) {
        console.error('[getCompanyFinancialSummary] 獲取 other_transactions 明細失敗:', otherTxError);
      }

      const expensesTotal =
        (expensesSumRows || []).reduce((sum: number, e: any) => sum + Number(e.amount || 0), 0);
      const otherExpenseTotal =
        (otherTxSumRows || []).reduce((sum: number, e: any) => sum + Number(e.amount || 0), 0);

      const expenseLineItems =
        (expensesData || []).map((e: any) => ({
          id: e.id,
          amount: Number(e.amount || 0),
          category: String(e.category || ''),
          date: e.expense_date,
          description: e.description ?? null,
          vendor: e.vendor ?? null
        })) || [];

      const otherExpenseLineItems =
        (otherTxData || []).map((e: any) => ({
          id: e.id,
          amount: Number(e.amount || 0),
          category: String(e.category || ''),
          date: e.transaction_date,
          description: e.description ?? null
        })) || [];

      const totalExpenses = expensesTotal + otherExpenseTotal;
      const netProfit = totalRevenue - totalExpenses;
      const profitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

      // 獲取案件數量
      // ✅ 修復：使用 company_id 而不是 craftsman_id
      const { data: jobsData } = await supabase
        .from('service_requests')
        .select('id')
        .eq('company_id', companyId)
        .eq('status', 'completed');

      const completedJobs = jobsData?.length || 0;
      const averageRevenuePerJob = completedJobs > 0 ? totalRevenue / completedJobs : 0;

      // 月度增長：同上口徑，依 paid_at 優先歸屬月份
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const prevMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);

      const sumRevenueInRange = (start: Date, endExclusive: Date) =>
        (approvedRows || []).reduce((sum, payment) => {
          const d = companyFinancePaymentRecognizedAt(payment);
          if (!d || d < start || d >= endExclusive) return sum;
          return sum + companyFinancePaymentReceivedAmount(payment);
        }, 0);

      const currentMonthRevenue = sumRevenueInRange(monthStart, nextMonthStart);
      const previousMonthRevenue = sumRevenueInRange(prevMonthStart, monthStart);
      const monthlyGrowth =
        previousMonthRevenue > 0
          ? ((currentMonthRevenue - previousMonthRevenue) / previousMonthRevenue) * 100
          : currentMonthRevenue > 0
          ? 100
          : 0;

      return {
        totalRevenue,
        totalExpenses,
        netProfit,
        profitMargin,
        averageRevenuePerJob,
        monthlyGrowth,
        expenseLineItems,
        otherExpenseLineItems
      };
    } catch (error) {
      console.error('Error fetching company financial summary:', error);
      return {
        totalRevenue: 0,
        totalExpenses: 0,
        netProfit: 0,
        profitMargin: 0,
        averageRevenuePerJob: 0,
        monthlyGrowth: 0,
        expenseLineItems: [],
        otherExpenseLineItems: []
      };
    }
  }

  // 根據用戶ID獲取公司資料
  static async getCompanyByUserId(userId: string): Promise<any> {
    try {
      console.log('獲取公司資料，用戶ID:', userId);

      // 如果 userId 不是有效的 UUID（例如 temp-user-id），跳過查詢
      if (!isValidUUID(userId)) {
        console.log('用戶ID不是有效的UUID，跳過查詢');
        return null;
      }

      // 首先檢查用戶是否為公司類型
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, name, email, phone, user_type, profile_image')
        .eq('id', userId)
        .single();

      if (userError || !userData) {
        console.error('用戶不存在:', userError);
        return null;
      }

      console.log('用戶資料:', userData);

      // 允許公司類型和行政部員工類型
      if (userData.user_type === 'company') {
        // 公司類型：直接從 companies 表中查找公司記錄
        let companyData: any = null;
        const { data: initialCompanyData, error: companyError } = await supabase
          .from('companies')
          .select('*')
          .eq('user_id', userId)
          .single();
        
        companyData = initialCompanyData;

        if (companyError) {
          // 檢查錯誤類型：PGRST116 表示記錄不存在
          const isRecordNotFound = companyError.code === 'PGRST116' || 
                                   companyError.message?.includes('No rows') ||
                                   companyError.message?.includes('not found');
          
          if (isRecordNotFound) {
            console.log('公司記錄不存在，嘗試創建新記錄...');
            // 如果沒有公司記錄，創建一個基本的公司記錄
            const { data: newCompanyData, error: createError } = await supabase
              .from('companies')
              .insert({
                user_id: userId,
                company_name: userData.name,
                tax_id: 'TEMP_TAX_ID', // 臨時稅號
                description: `${userData.name} 公司`,
                status: 'active',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              })
              .select()
              .single();

            if (createError) {
              // 如果是唯一約束衝突（記錄已存在），嘗試重新查詢
              if (createError.code === '23505' || createError.message?.includes('duplicate key')) {
                console.log('⚠️ 公司記錄已存在（唯一約束衝突），重新查詢...');
                const { data: existingCompanyData, error: retryError } = await supabase
                  .from('companies')
                  .select('*')
                  .eq('user_id', userId)
                  .maybeSingle();
                
                if (retryError || !existingCompanyData) {
                  console.error('重新查詢公司記錄失敗:', retryError);
                  return null;
                }
                
                console.log('✅ 成功獲取已存在的公司記錄');
                companyData = existingCompanyData;
              } else {
                console.error('創建公司記錄失敗:', createError);
                return null;
              }
            } else if (newCompanyData) {
              console.log('✅ 創建了新的公司記錄:', newCompanyData);
              companyData = newCompanyData;
            }
          } else {
            // 其他錯誤（如 RLS 權限問題），記錄錯誤但不創建
            console.error('查詢公司記錄時發生錯誤（非記錄不存在）:', companyError);
            return null;
          }
        }
        
        // 如果沒有公司記錄，返回 null
        if (!companyData) {
          console.error('無法獲取公司記錄');
          return null;
        }

        // 返回公司資料
        return {
          id: companyData.id,
          user_id: companyData.user_id,
          name: userData.name,
          email: userData.email,
          phone: userData.phone,
          profileImage: this.convertProfileImageUrl(userData.profile_image),
          company_name: companyData.company_name,
          description: companyData.description,
          status: companyData.status,
          employee_count: companyData.employee_count || 0,
          established_date: companyData.established_date,
          website: companyData.website,
          address: companyData.address,
          business_license: companyData.business_license,
          tax_id: companyData.tax_id,
          created_at: companyData.created_at,
          updated_at: companyData.updated_at
        };
      } else if (userData.user_type === 'administrative_staff' || userData.user_type === 'finance_staff' || userData.user_type === 'other_staff') {
        // 員工類型：通過 employees 表找到所屬公司
        console.log('用戶是員工類型，查找所屬公司...');
        
        // 從 employees 表中查找該員工記錄（使用 user_id 更準確）
        const { data: employeeData, error: employeeError } = await supabase
          .from('employees')
          .select('company_id')
          .eq('user_id', userId)
          .single();

        if (employeeError || !employeeData || !employeeData.company_id) {
          console.error('找不到員工記錄或公司ID:', employeeError);
          return null;
        }

        // 通過 company_id 查找公司記錄
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select('*')
          .eq('id', employeeData.company_id)
          .single();

        if (companyError || !companyData) {
          console.error('找不到公司記錄:', companyError);
          return null;
        }

        console.log('找到員工所屬公司:', companyData);

        // 返回公司資料
        return {
          id: companyData.id,
          user_id: companyData.user_id,
          name: companyData.company_name || userData.name,
          email: userData.email,
          phone: userData.phone,
          profileImage: this.convertProfileImageUrl(userData.profile_image),
          company_name: companyData.company_name,
          description: companyData.description,
          status: companyData.status,
          employee_count: companyData.employee_count || 0,
          established_date: companyData.established_date,
          website: companyData.website,
          address: companyData.address,
          business_license: companyData.business_license,
          tax_id: companyData.tax_id,
          created_at: companyData.created_at,
          updated_at: companyData.updated_at
        };
      } else {
        console.error('用戶類型不允許訪問公司資料:', userData.user_type);
        return null;
      }
    } catch (error) {
      console.error('Error fetching company by user ID:', error);
      return null;
    }
  }

  // 獲取工匠評價列表（避免複雜 JOIN，拆成兩次查詢，防止超時）
  static async getCraftsmanReviews(craftsmanId: string): Promise<any[]> {
    try {
      console.log('獲取工匠評價，工匠ID:', craftsmanId);

      // 1️⃣ 先只查 reviews，本身不 join users，讓查詢盡量簡單快速
      const { data: reviewsData, error } = await supabase
        .from('reviews')
        .select(`
          id,
          rating,
          comment,
          created_at,
          service_request_id,
          customer_id
        `)
        .eq('craftsman_id', craftsmanId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error fetching craftsman reviews:', error);
        return [];
      }

      if (!reviewsData || reviewsData.length === 0) {
        return [];
      }

      // 2️⃣ 收集所有 customer_id，一次性查出對應的使用者資料，避免每筆評價各查一次
      const customerIds = Array.from(
        new Set(
          reviewsData
            .map((r: any) => r.customer_id)
            .filter((id: string | null) => !!id)
        )
      );

      let customersById: Record<
        string,
        { id: string; name: string; profile_image: string | null }
      > = {};

      if (customerIds.length > 0) {
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, name, profile_image')
          .in('id', customerIds as string[]);

        if (usersError) {
          console.error('Error fetching review customers:', usersError);
        } else if (usersData) {
          customersById = usersData.reduce(
            (
              acc: Record<
                string,
                { id: string; name: string; profile_image: string | null }
              >,
              user: any
            ) => {
              acc[user.id] = {
                id: user.id,
                name: user.name,
                profile_image: user.profile_image || null,
              };
              return acc;
            },
            {}
          );
        }
      }

      console.log('獲取到的評價數據:', reviewsData);

      // 3️⃣ 組合前端需要的格式，保持與原有結構相容
      const formattedReviews =
        reviewsData.map((review: any) => {
          const customer =
            (review.customer_id && customersById[review.customer_id]) || null;

          return {
            id: review.id,
            rating: review.rating,
            comment: review.comment,
            createdAt: review.created_at
              ? new Date(review.created_at)
              : undefined,
            serviceRequestId: review.service_request_id,
            reviewer: {
              id: customer?.id || review.customer_id || '',
              name: customer?.name || '匿名用戶',
              profileImage: this.convertProfileImageUrl(customer?.profile_image),
            },
          };
        }) || [];

      return formattedReviews;
    } catch (error) {
      console.error('Error fetching craftsman reviews:', error);
      return [];
    }
  }

  // 添加数据验证方法
  static async validateCraftsmanData(id: string): Promise<{
    isValid: boolean;
    data: any;
    error: string | null;
  }> {
    try {
      console.log('=== 验证工匠数据 ===');
      console.log('验证ID:', id);
      
      // 直接查询数据库
      const { data, error } = await supabase
        .from('craftsmen')
        .select('*')
        .eq('id', id)
        .single();
      
      console.log('数据库查询结果:', { data, error });
      
      if (error) {
        return {
          isValid: false,
          data: null,
          error: error.message
        };
      }
      
      if (!data) {
        return {
          isValid: false,
          data: null,
          error: '工匠不存在'
        };
      }
      
      return {
        isValid: true,
        data: data,
        error: null
      };
    } catch (err) {
      console.error('验证失败:', err);
      return {
        isValid: false,
        data: null,
        error: '验证过程出错'
      };
    }
  }

  // 修復 getCompanyStats 方法
  static async getCompanyStats(companyId: string): Promise<any> {
    try {
      // 首先檢查這是公司還是工匠
      const { data: companyData } = await supabase
        .from('companies')
        .select('employee_count, hourly_rate')
        .eq('id', companyId)
        .single();

      if (companyData) {
        // 這是公司記錄
        const totalEmployees = companyData.employee_count || 0;
        const hourlyRate = companyData.hourly_rate || 0;
        
        // 獲取其他統計數據...
        return {
          totalEarnings: 0,
          completedJobs: 0,
          pendingJobs: 0,
          averageRating: 0,
          totalReviews: 0,
          totalCustomers: 0,
          totalEmployees,
          responseRate: 0
        };
      } else {
        // 檢查是否是工匠記錄
        const { data: craftsmanData } = await supabase
          .from('craftsmen')
          .select('employee_count, hourly_rate')
          .eq('id', companyId)
          .single();

        if (craftsmanData) {
          const totalEmployees = craftsmanData.employee_count || 0;
          const hourlyRate = craftsmanData.hourly_rate || 0;
          
          return {
            totalEarnings: 0,
            completedJobs: 0,
            pendingJobs: 0,
            averageRating: 0,
            totalReviews: 0,
            totalCustomers: 0,
            totalEmployees,
            responseRate: 0
          };
        }
      }

      // 如果都不是，返回默認值
      return {
        totalEarnings: 0,
        completedJobs: 0,
        pendingJobs: 0,
        averageRating: 0,
        totalReviews: 0,
        totalCustomers: 0,
        totalEmployees: 0,
        responseRate: 0
      };
    } catch (error) {
      console.error('Error in getCompanyStats:', error);
      return {
        totalEarnings: 0,
        completedJobs: 0,
        pendingJobs: 0,
        averageRating: 0,
        totalReviews: 0,
        totalCustomers: 0,
        totalEmployees: 0,
        responseRate: 0
      };
    }
  }

  // 為已完成但沒有付款記錄的案件創建付款記錄
  static async createMissingPaymentRecords(craftsmanId: string): Promise<{ created: number; errors: string[] }> {
    try {
      console.log('開始修復工匠的缺失付款記錄:', craftsmanId);
      
      // 獲取已完成但沒有付款記錄的案件
      const { data: completedJobs, error: jobsError } = await supabase
        .from('service_requests')
        .select(`
          id,
          user_id,
          estimated_price,
          actual_price,
          title,
          created_at,
          completed_at
        `)
        .eq('craftsman_id', craftsmanId)
        .eq('status', 'completed');

      if (jobsError) {
        console.error('獲取已完成案件失敗:', jobsError);
        return { created: 0, errors: [jobsError.message] };
      }

      if (!completedJobs || completedJobs.length === 0) {
        console.log('沒有找到已完成的案件');
        return { created: 0, errors: [] };
      }

      console.log(`找到 ${completedJobs.length} 個已完成案件`);

      // 檢查哪些案件沒有付款記錄
      const jobIds = completedJobs.map(job => job.id);
      const { data: existingPayments, error: paymentsError } = await supabase
        .from('payments')
        .select('request_id')
        .in('request_id', jobIds);

      if (paymentsError) {
        console.error('檢查現有付款記錄失敗:', paymentsError);
        return { created: 0, errors: [paymentsError.message] };
      }

      const existingPaymentIds = new Set(existingPayments?.map(p => p.request_id) || []);
      const jobsWithoutPayments = completedJobs.filter(job => !existingPaymentIds.has(job.id));

      console.log(`找到 ${jobsWithoutPayments.length} 個沒有付款記錄的案件`);

      if (jobsWithoutPayments.length === 0) {
        return { created: 0, errors: [] };
      }

      // 獲取工匠信息用於佣金計算
      const craftsman = await this.getCraftsmanById(craftsmanId);
      if (!craftsman) {
        return { created: 0, errors: ['找不到工匠信息'] };
      }

      let created = 0;
      const errors: string[] = [];

      // 為每個缺失的案件創建付款記錄
      for (const job of jobsWithoutPayments) {
        try {
          const amount = job.actual_price || job.estimated_price || 0;
          if (amount <= 0) {
            console.log(`跳過金額為 0 的案件: ${job.id}`);
            continue;
          }

          // 計算佣金
          const commissionDetails = CommissionService.getCommissionDetails(
            amount,
            {
              ...(craftsman as { isPremium: boolean }),
              isCompanyFixed: CraftsmanService.rowIsCompanyFixed(
                job as { pricing_mode?: string; company_id?: string; craftsman_id?: string }
              ),
            },
            false // 假設不是緊急服務
          );

          // 創建付款記錄
          const { error: insertError } = await supabase
            .from('payments')
            .insert({
              request_id: job.id,
              craftsman_id: craftsmanId,
              amount: amount,
              deposit_amount: amount * 0.3, // 30% 訂金
              remaining_amount: amount * 0.7, // 70% 尾款
              payment_method: 'cash', // 假設為現金支付
              transaction_id: `REPAIR_${job.id}_${Date.now()}`,
              status: 'completed',
              commission_amount: commissionDetails.commission,
              craftsman_earnings: commissionDetails.craftsmanEarnings,
              platform_fee: commissionDetails.platformFee || 0,
              created_at: job.completed_at || job.created_at,
              updated_at: new Date().toISOString()
            });

          if (insertError) {
            console.error(`創建付款記錄失敗 (案件 ${job.id}):`, insertError);
            errors.push(`案件 ${job.title}: ${insertError.message}`);
          } else {
            console.log(`成功創建付款記錄: 案件 ${job.id}, 金額 NT$ ${amount}`);
            created++;
          }
        } catch (error) {
          console.error(`處理案件 ${job.id} 時發生錯誤:`, error);
          errors.push(`案件 ${job.title}: ${error.message}`);
        }
      }

      console.log(`修復完成: 創建了 ${created} 個付款記錄, ${errors.length} 個錯誤`);
      return { created, errors };
    } catch (error) {
      console.error('修復付款記錄時發生錯誤:', error);
      return { created: 0, errors: [error.message] };
    }
  }
}
